/**
 * Admin — `/admin/premium-invite-config`
 *
 * Persiste en `premium_invite_config` (Postgres): A/B invitación premium, Reels, CTAs.
 */

import { AdminPageHeader } from "@/components/admin/AdminPageHeader";
import { AdminPageShell } from "@/components/admin/AdminPageShell";
import { ConfigOwnerCallout } from "@/components/admin/ConfigOwnerCallout";
import { HelpHint } from "@/components/admin/HelpHint";
import { BRAND_FAN_URL, BRAND_TELEGRAM_URL } from "@/lib/brandConfig";
import { useAdminAuth } from "@/hooks/useAdminAuth";
import { useToast } from "@/hooks/use-toast";
import {
  type PremiumInvitePublicConfig,
  type ReelsPlaybackSource,
  type ReelsPlayerVariant,
  PREMIUM_INVITE_MAX_CURATED_REELS_IDS,
  broadcastPremiumInviteConfigUpdated,
  parseMuxPlaybackIdsFromEditorText,
  parsePremiumInvitePublicConfig,
  summarizeCuratedPlaybackEditor,
} from "@/lib/premiumInvitePublicConfig";
import { readJsonResponse } from "@/lib/readJsonResponse";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { MuxVideoPicker } from "@/components/admin/MuxVideoPicker";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { cn } from "@/lib/utils";
import { Textarea } from "@/components/ui/textarea";
import { AlertTriangle, CheckCircle2, ChevronDown, ExternalLink, Info, Languages, Layers, Loader2, MessageCircle, RefreshCw, RotateCcw, Save, Sparkles, Tv2, Type, XCircle } from "lucide-react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useLocation } from "react-router-dom";

type ForceLayoutOpt = "auto" | "premium_simple" | "reels_feed";
type MuxTriState = "inherit" | "on" | "off";

function formatFreeTelegramByCountry(map: Record<string, string>): string {
  return Object.entries(map)
    .filter(([, url]) => url.trim())
    .map(([code, url]) => `${code.trim().toUpperCase()}: ${url.trim()}`)
    .join("\n");
}

function parseFreeTelegramByCountryEditor(text: string): Record<string, string> | null {
  const trimmed = text.trim();
  if (!trimmed) return null;
  const out: Record<string, string> = {};
  for (const line of trimmed.split("\n")) {
    const t = line.trim();
    if (!t || t.startsWith("#") || t.startsWith("//")) continue;
    const idx = t.indexOf(":");
    if (idx < 2) continue;
    const code = t.slice(0, idx).trim().toUpperCase();
    const url = t.slice(idx + 1).trim();
    if (!/^[A-Z]{2}$/.test(code)) continue;
    if (!url) continue;
    out[code] = url;
  }
  return Object.keys(out).length > 0 ? out : null;
}

function muxTriFromConfig(v: boolean | null | undefined): MuxTriState {
  if (v === true) return "on";
  if (v === false) return "off";
  return "inherit";
}

function muxTriToPayload(t: MuxTriState): boolean | null {
  if (t === "inherit") return null;
  return t === "on";
}

function configFingerprint(c: PremiumInvitePublicConfig): string {
  return JSON.stringify({
    experiment_enabled: c.experiment_enabled,
    use_posthog_layout: c.use_posthog_layout,
    force_layout: c.force_layout,
    reels_rollout_percent: c.reels_rollout_percent,
    reels_teaser_count: c.reels_teaser_count,
    reels_player_variant: c.reels_player_variant,
    reels_playback_source: c.reels_playback_source,
    reels_curated_playback_ids: c.reels_curated_playback_ids,
    free_telegram_url: c.free_telegram_url ?? null,
    free_telegram_url_by_country: c.free_telegram_url_by_country ?? null,
    free_telegram_enabled: c.free_telegram_enabled ?? null,
    vip_checkout_base_url: c.vip_checkout_base_url ?? null,
    end_screen_headline: c.end_screen_headline ?? null,
    end_screen_free_cta_label: c.end_screen_free_cta_label ?? null,
    end_screen_vip_cta_label: c.end_screen_vip_cta_label ?? null,
    mux_cta_friendly_banner: muxTriFromConfig(c.mux_cta_friendly_banner),
    mux_cta_free_join_copy: muxTriFromConfig(c.mux_cta_free_join_copy),
    friendly_vip_badge_text: c.friendly_vip_badge_text ?? null,
    friendly_vip_disclaimer_text: c.friendly_vip_disclaimer_text ?? null,
    simple_show_telegram_card: muxTriFromConfig(c.simple_show_telegram_card),
    simple_show_vip_card: muxTriFromConfig(c.simple_show_vip_card),
    simple_telegram_card_locked: c.simple_telegram_card_locked,
    simple_vip_card_locked: c.simple_vip_card_locked,
    simple_telegram_card_title: c.simple_telegram_card_title ?? null,
    simple_telegram_card_cta: c.simple_telegram_card_cta ?? null,
    simple_vip_card_title: c.simple_vip_card_title ?? null,
    simple_vip_card_subtitle: c.simple_vip_card_subtitle ?? null,
    simple_vip_card_cta: c.simple_vip_card_cta ?? null,
    simple_hero_badge: c.simple_hero_badge ?? null,
    simple_hero_title: c.simple_hero_title ?? null,
    simple_hero_subtitle: c.simple_hero_subtitle ?? null,
    simple_hero_trust_line: c.simple_hero_trust_line ?? null,
    simple_hero_path_line: c.simple_hero_path_line ?? null,
    simple_free_telegram_cta: c.simple_free_telegram_cta ?? null,
    simple_free_telegram_badge: c.simple_free_telegram_badge ?? null,
    simple_free_telegram_intro: c.simple_free_telegram_intro ?? null,
    simple_free_telegram_subline: c.simple_free_telegram_subline ?? null,
    simple_vip_recommended_badge: c.simple_vip_recommended_badge ?? null,
    simple_membership_video_cta: c.simple_membership_video_cta ?? null,
    simple_vip_tap_hint: c.simple_vip_tap_hint ?? null,
    simple_telegram_activation_hint: c.simple_telegram_activation_hint ?? null,
    simple_footer_trust_line: c.simple_footer_trust_line ?? null,
    simple_texts_by_lang: c.simple_texts_by_lang ?? null,
    simple_mux_overlay_enabled: c.simple_mux_overlay_enabled,
    simple_mux_overlay_playback_id: c.simple_mux_overlay_playback_id ?? null,
    vip_price_usd: c.vip_price_usd ?? null,
    vip_price_ars: c.vip_price_ars ?? null,
    standard_price_usd: c.standard_price_usd ?? null,
    standard_price_ars: c.standard_price_ars ?? null,
    telegram_basic_price_cents: c.telegram_basic_price_cents ?? null,
    ar_invitacion_standard_price_ars: c.ar_invitacion_standard_price_ars ?? null,
    ar_invitacion_vip_price_ars: c.ar_invitacion_vip_price_ars ?? null,
    vip_donation_suggested_usd: c.vip_donation_suggested_usd ?? null,
    sorprendeme_min_amount: c.sorprendeme_min_amount ?? null,
    sorprendeme_amount_tiers: c.sorprendeme_amount_tiers ?? null,
    free_access_cta_enabled: c.free_access_cta_enabled === true,
    free_access_cta_label: c.free_access_cta_label ?? null,
  });
}

function buildFormFingerprint(args: {
  experimentEnabled: boolean;
  usePosthogLayout: boolean;
  forceLayout: ForceLayoutOpt;
  reelsRollout: number;
  teaserCount: number;
  playerVariant: ReelsPlayerVariant;
  reelsPlaybackSource: ReelsPlaybackSource;
  curatedPlaybackText: string;
  freeTelegramUrl: string;
  freeTelegramUrlByCountry: Record<string, string>;
  freeTelegramByCountryEditor: string;
  freeTelegramEnabled: boolean;
  dualCtaFreeChannelLabel: string;
  vipBaseUrl: string;
  endHeadline: string;
  endFreeLbl: string;
  endVipLbl: string;
  muxFriendlyBanner: MuxTriState;
  muxFreeJoinCopy: MuxTriState;
  friendlyBadgeText: string;
  friendlyDisclaimerText: string;
  simpleShowTelegramCard: MuxTriState;
  simpleShowVipCard: MuxTriState;
  simpleTelegramCardLocked: boolean;
  simpleVipCardLocked: boolean;
  simpleTelegramCardTitle: string;
  simpleTelegramCardCta: string;
  simpleVipCardTitle: string;
  simpleVipCardSubtitle: string;
  simpleVipCardCta: string;
  simpleHeroBadge: string;
  simpleHeroTitle: string;
  simpleHeroSubtitle: string;
  simpleHeroTrustLine: string;
  simpleHeroPathLine: string;
  simpleFreeTelegramCta: string;
  simpleFreeTelegramBadge: string;
  simpleFreeTelegramIntro: string;
  simpleFreeTelegramSubline: string;
  simpleVipRecommendedBadge: string;
  simpleMembershipVideoCta: string;
  simpleVipTapHint: string;
  simpleTelegramActivationHint: string;
  simpleFooterTrustLine: string;
  simpleTextsByLang: Record<string, Record<string, string>>;
  simpleMuxOverlayEnabled: boolean;
  simpleMuxOverlayPlaybackId: string;
  vipPriceUsd: string;
  vipPriceArs: string;
  standardPriceUsd: string;
  standardPriceArs: string;
  telegramBasicPriceCents: string;
  arInvitacionStandardPriceArs: string;
  arInvitacionVipPriceArs: string;
  vipDonationSuggestedUsd: string;
  sorprendemeMinAmount: string;
  sorprendemeAmountTiers: string;
  freeAccessCtaEnabled: boolean;
  freeAccessCtaLabel: string;
}): string {
  const ids =
    args.reelsPlaybackSource === "curated"
      ? parseMuxPlaybackIdsFromEditorText(args.curatedPlaybackText)
      : [];
  return JSON.stringify({
    experiment_enabled: args.experimentEnabled,
    use_posthog_layout: args.usePosthogLayout,
    force_layout: args.forceLayout === "auto" ? null : args.forceLayout,
    reels_rollout_percent: args.reelsRollout,
    reels_teaser_count: args.teaserCount,
    reels_player_variant: args.playerVariant,
    reels_playback_source: args.reelsPlaybackSource,
    reels_curated_playback_ids: ids,
    free_telegram_url: args.freeTelegramUrl.trim() || null,
    free_telegram_url_by_country: Object.keys(args.freeTelegramUrlByCountry).length > 0 ? args.freeTelegramUrlByCountry : null,
    free_telegram_enabled: args.freeTelegramEnabled,
    dual_cta_free_channel_label: args.dualCtaFreeChannelLabel.trim() || null,
    vip_checkout_base_url: args.vipBaseUrl.trim() || null,
    end_screen_headline: args.endHeadline.trim() || null,
    end_screen_free_cta_label: args.endFreeLbl.trim() || null,
    end_screen_vip_cta_label: args.endVipLbl.trim() || null,
    mux_cta_friendly_banner: args.muxFriendlyBanner,
    mux_cta_free_join_copy: args.muxFreeJoinCopy,
    friendly_vip_badge_text: args.friendlyBadgeText.trim() || null,
    friendly_vip_disclaimer_text: args.friendlyDisclaimerText.trim() || null,
    simple_show_telegram_card: args.simpleShowTelegramCard,
    simple_show_vip_card: args.simpleShowVipCard,
    simple_telegram_card_locked: args.simpleTelegramCardLocked,
    simple_vip_card_locked: args.simpleVipCardLocked,
    simple_telegram_card_title: args.simpleTelegramCardTitle.trim() || null,
    simple_telegram_card_cta: args.simpleTelegramCardCta.trim() || null,
    simple_vip_card_title: args.simpleVipCardTitle.trim() || null,
    simple_vip_card_subtitle: args.simpleVipCardSubtitle.trim() || null,
    simple_vip_card_cta: args.simpleVipCardCta.trim() || null,
    simple_hero_badge: args.simpleHeroBadge.trim() || null,
    simple_hero_title: args.simpleHeroTitle.trim() || null,
    simple_hero_subtitle: args.simpleHeroSubtitle.trim() || null,
    simple_hero_trust_line: args.simpleHeroTrustLine.trim() || null,
    simple_hero_path_line: args.simpleHeroPathLine.trim() || null,
    simple_free_telegram_cta: args.simpleFreeTelegramCta.trim() || null,
    simple_free_telegram_badge: args.simpleFreeTelegramBadge.trim() || null,
    simple_free_telegram_intro: args.simpleFreeTelegramIntro.trim() || null,
    simple_free_telegram_subline: args.simpleFreeTelegramSubline.trim() || null,
    simple_vip_recommended_badge: args.simpleVipRecommendedBadge.trim() || null,
    simple_membership_video_cta: args.simpleMembershipVideoCta.trim() || null,
    simple_vip_tap_hint: args.simpleVipTapHint.trim() || null,
    simple_telegram_activation_hint: args.simpleTelegramActivationHint.trim() || null,
    simple_footer_trust_line: args.simpleFooterTrustLine.trim() || null,
    simple_texts_by_lang: Object.keys(args.simpleTextsByLang).length > 0 ? JSON.stringify(args.simpleTextsByLang) : null,
    simple_mux_overlay_enabled: args.simpleMuxOverlayEnabled,
    simple_mux_overlay_playback_id: args.simpleMuxOverlayPlaybackId.trim() || null,
    vip_price_usd: (() => { const n = parseFloat(args.vipPriceUsd); return Number.isFinite(n) && n > 0 ? n : null; })(),
    vip_price_ars: (() => { const n = parseFloat(args.vipPriceArs); return Number.isFinite(n) && n > 0 ? n : null; })(),
    standard_price_usd: (() => { const n = parseFloat(args.standardPriceUsd); return Number.isFinite(n) && n > 0 ? n : null; })(),
    standard_price_ars: (() => { const n = parseFloat(args.standardPriceArs); return Number.isFinite(n) && n > 0 ? n : null; })(),
    telegram_basic_price_cents: (() => { const n = parseFloat(args.telegramBasicPriceCents); return Number.isFinite(n) && n > 0 ? n : null; })(),
    ar_invitacion_standard_price_ars: (() => { const n = parseFloat(args.arInvitacionStandardPriceArs); return Number.isFinite(n) && n > 0 ? n : null; })(),
    ar_invitacion_vip_price_ars: (() => { const n = parseFloat(args.arInvitacionVipPriceArs); return Number.isFinite(n) && n > 0 ? n : null; })(),
    vip_donation_suggested_usd: (() => { const n = parseFloat(args.vipDonationSuggestedUsd); return Number.isFinite(n) && n > 0 ? n : null; })(),
    sorprendeme_min_amount: (() => { const n = parseFloat(args.sorprendemeMinAmount); return Number.isFinite(n) && n > 0 ? n : null; })(),
    sorprendeme_amount_tiers: args.sorprendemeAmountTiers.trim() || null,
    free_access_cta_enabled: args.freeAccessCtaEnabled,
    free_access_cta_label: args.freeAccessCtaLabel.trim() || null,
  });
}

function parseAdminResponse(data: unknown): PremiumInvitePublicConfig | null {
  if (data === null || typeof data !== "object" || Array.isArray(data)) return null;
  if (!("config" in data)) return null;
  return parsePremiumInvitePublicConfig((data as { config: unknown }).config);
}

function formatServerDate(iso: string | null): string | null {
  if (!iso?.trim()) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleString("es-AR", { dateStyle: "medium", timeStyle: "short" });
}

function clampRollout(n: number, fallback: number): number {
  if (!Number.isFinite(n)) return fallback;
  return Math.min(100, Math.max(0, Math.round(n)));
}

function clampTeaserCount(n: number, fallback: number): number {
  if (!Number.isFinite(n)) return fallback;
  return Math.min(20, Math.max(1, Math.round(n)));
}

/** Parse a VITE_* env boolean string (matches BioTemplatePremiumSimple parseBooleanEnv). */
function parseBoolEnv(raw: string | undefined, fallback: boolean): boolean {
  if (raw === undefined) return fallback;
  const v = raw.trim().toLowerCase();
  if (["1", "true", "yes", "on", "active"].includes(v)) return true;
  if (["0", "false", "no", "off", "inactive"].includes(v)) return false;
  return fallback;
}

/** Computes the value the template uses when an admin tri-state is "inherit". */
const INHERITED_FROM_ENV = {
  showTelegramCard: parseBoolEnv(
    import.meta.env.VITE_PREMIUM_SIMPLE_SHOW_TELEGRAM_CARD as string | undefined,
    true,
  ),
  showVipCard: parseBoolEnv(
    import.meta.env.VITE_PREMIUM_SIMPLE_SHOW_VIP_CARD as string | undefined,
    true,
  ),
} as const;

function inheritLabel(value: boolean): string {
  return `Heredar env · actualmente ${value ? "Mostrar" : "Ocultar"}`;
}

/** Cadena de fallback para `free_telegram_url` (replica el resolver runtime). */
const EFFECTIVE_FREE_TELEGRAM_URL =
  (import.meta.env.VITE_PREMIUM_SIMPLE_FREE_TELEGRAM_URL as string | undefined)?.trim() ||
  (import.meta.env.VITE_TELEGRAM_FREE_GROUP_URL as string | undefined)?.trim() ||
  BRAND_TELEGRAM_URL ||
  "";

/** Cadena de fallback para `vip_checkout_base_url` (replica el resolver runtime). */
const EFFECTIVE_VIP_BASE_URL = BRAND_FAN_URL
  ? `${BRAND_FAN_URL.replace(/\/$/, "")}/subscripcion`
  : "/subscripcion";

/** ENV de prerrequisitos del template. Replica la misma cadena de resolución que el runtime del template. */
const TEMPLATE_PREREQS = {
  /** Alguno de los tres orígenes que resuelve el template (mismo orden que el runtime). */
  hasVipMux: Boolean(
    (import.meta.env.VITE_PREMIUM_SIMPLE_MUX_PLAYBACK_ID as string | undefined)?.trim() ||
    (import.meta.env.VITE_PREMIUM_MUX_ES_VIP as string | undefined)?.trim() ||
    (import.meta.env.VITE_PREMIUM_MUX_EN_VIP as string | undefined)?.trim(),
  ),
  hasTelegramPrivateUrl: Boolean((import.meta.env.VITE_TELEGRAM_PRIVATE_GROUP_URL as string | undefined)?.trim()),
  hasMpCheckoutAr: Boolean((import.meta.env.VITE_MP_CHECKOUT_BASIC_AR as string | undefined)?.trim()),
} as const;

interface FunnelRiskFlag {
  id: string;
  label: string;
  detail: string;
}

/** Calcula los riesgos del funnel basándose en el estado actual del form admin. */
function analyzeAdminFunnelRisks(args: {
  simpleShowTelegramCard: MuxTriState;
  simpleShowVipCard: MuxTriState;
  freeTelegramUrl: string;
}): FunnelRiskFlag[] {
  const risks: FunnelRiskFlag[] = [];

  const showTg =
    args.simpleShowTelegramCard === "on" ||
    (args.simpleShowTelegramCard === "inherit" && INHERITED_FROM_ENV.showTelegramCard);
  const showVip =
    args.simpleShowVipCard === "on" ||
    (args.simpleShowVipCard === "inherit" && INHERITED_FROM_ENV.showVipCard);

  if (showVip && !TEMPLATE_PREREQS.hasVipMux) {
    risks.push({
      id: "vip_card_visible_no_mux",
      label: "Card VIP visible sin video Mux",
      detail: "VITE_PREMIUM_SIMPLE_MUX_PLAYBACK_ID no está configurado en Railway. La card aparecerá sin video.",
    });
  }
  if (showTg && !TEMPLATE_PREREQS.hasTelegramPrivateUrl) {
    risks.push({
      id: "telegram_card_visible_no_private_url",
      label: "Card Telegram visible sin URL privada",
      detail: "VITE_TELEGRAM_PRIVATE_GROUP_URL no está configurado. El usuario post-pago no recibirá invite.",
    });
  }
  if (showTg && !TEMPLATE_PREREQS.hasMpCheckoutAr) {
    risks.push({
      id: "ar_basic_mp_url_missing_stripe_fallback",
      label: "AR sin URL MercadoPago — cae a Stripe (USD)",
      detail: "VITE_MP_CHECKOUT_BASIC_AR no está configurado. Usuarios AR pagarán en Stripe USD en vez de MP ARS.",
    });
  }

  const effectiveFreeTelegram = args.freeTelegramUrl.trim() || EFFECTIVE_FREE_TELEGRAM_URL;
  if (!showTg && !showVip && !effectiveFreeTelegram) {
    risks.push({
      id: "all_ctas_disabled",
      label: "Sin ningún CTA accionable",
      detail: "Card Telegram, Card VIP y canal gratis están todas vacías. El usuario verá la página sin acciones.",
    });
  }

  return risks;
}

type ActiveLang = "es" | "en" | "pt" | "fr" | "it";

const LANG_LABELS: Record<ActiveLang, string> = {
  es: "🇪🇸 ES",
  en: "🇬🇧 EN",
  pt: "🇧🇷 PT",
  fr: "🇫🇷 FR",
  it: "🇮🇹 IT",
};

const SURFACE_BADGE_STYLES = {
  simple:  { label: "Landing simple",  cls: "bg-sky-950/70 text-sky-300 border-sky-800/60" },
  home:    { label: "Home",            cls: "bg-violet-950/70 text-violet-300 border-violet-800/60" },
  reels:   { label: "Reels feed",      cls: "bg-indigo-950/70 text-indigo-300 border-indigo-800/60" },
  overlay: { label: "Overlay video",   cls: "bg-amber-950/70 text-amber-300 border-amber-800/60" },
} as const;

type SurfaceKey = keyof typeof SURFACE_BADGE_STYLES;

function SurfaceBadge({ surface }: { surface: SurfaceKey }) {
  const { label, cls } = SURFACE_BADGE_STYLES[surface];
  return (
    <span className={`inline-flex items-center rounded-full border px-2 py-0.5 text-[10px] font-medium leading-none ${cls}`}>
      {label}
    </span>
  );
}

export default function PremiumInviteAdmin() {
  const { getAuthHeaders, isLoaded } = useAdminAuth();
  const { toast } = useToast();
  const [loading, setLoading] = useState(true);
  const [refetching, setRefetching] = useState(false);
  const [saving, setSaving] = useState(false);

  const [experimentEnabled, setExperimentEnabled] = useState(true);
  const [usePosthogLayout, setUsePosthogLayout] = useState(true);
  const [forceLayout, setForceLayout] = useState<ForceLayoutOpt>("auto");
  const [reelsRollout, setReelsRollout] = useState(0);
  const [teaserCount, setTeaserCount] = useState(3);
  const [playerVariant, setPlayerVariant] = useState<ReelsPlayerVariant>("three_teaser");
  const [reelsPlaybackSource, setReelsPlaybackSource] = useState<ReelsPlaybackSource>("mux_api");
  const [curatedPlaybackText, setCuratedPlaybackText] = useState("");
  const [freeTelegramUrl, setFreeTelegramUrl] = useState("");
  const [freeTelegramUrlByCountry, setFreeTelegramUrlByCountry] = useState<Record<string, string>>({});
  const [freeTelegramByCountryEditor, setFreeTelegramByCountryEditor] = useState("");
  /** Toggle maestro del Elemento free-telegram. true (default) = visible; false = oculto en todo el sitio. */
  const [freeTelegramEnabled, setFreeTelegramEnabled] = useState(true);
  const [dualCtaFreeChannelLabel, setDualCtaFreeChannelLabel] = useState("");
  const [vipBaseUrl, setVipBaseUrl] = useState("");
  const [endHeadline, setEndHeadline] = useState("");
  const [endFreeLbl, setEndFreeLbl] = useState("");
  const [endVipLbl, setEndVipLbl] = useState("");
  const [muxFriendlyBanner, setMuxFriendlyBanner] = useState<MuxTriState>("inherit");
  const [muxFreeJoinCopy, setMuxFreeJoinCopy] = useState<MuxTriState>("inherit");
  const [friendlyBadgeText, setFriendlyBadgeText] = useState("");
  const [friendlyDisclaimerText, setFriendlyDisclaimerText] = useState("");
  const [simpleShowTelegramCard, setSimpleShowTelegramCard] = useState<MuxTriState>("inherit");
  const [simpleShowVipCard, setSimpleShowVipCard] = useState<MuxTriState>("inherit");
  const [dualCtaFreeTelegram, setDualCtaFreeTelegram] = useState<MuxTriState>("inherit");
  const [simpleTelegramCardLocked, setSimpleTelegramCardLocked] = useState(false);
  const [simpleVipCardLocked, setSimpleVipCardLocked] = useState(false);
  const [simpleTelegramCardTitle, setSimpleTelegramCardTitle] = useState("");
  const [simpleTelegramCardCta, setSimpleTelegramCardCta] = useState("");
  const [simpleVipCardTitle, setSimpleVipCardTitle] = useState("");
  const [simpleVipCardSubtitle, setSimpleVipCardSubtitle] = useState("");
  const [simpleVipCardCta, setSimpleVipCardCta] = useState("");
  const [simpleHeroBadge, setSimpleHeroBadge] = useState("");
  const [simpleHeroTitle, setSimpleHeroTitle] = useState("");
  const [simpleHeroSubtitle, setSimpleHeroSubtitle] = useState("");
  const [simpleHeroTrustLine, setSimpleHeroTrustLine] = useState("");
  const [simpleHeroPathLine, setSimpleHeroPathLine] = useState("");
  const [simpleFreeTelegramCta, setSimpleFreeTelegramCta] = useState("");
  const [simpleFreeTelegramBadge, setSimpleFreeTelegramBadge] = useState("");
  const [simpleFreeTelegramIntro, setSimpleFreeTelegramIntro] = useState("");
  const [simpleFreeTelegramSubline, setSimpleFreeTelegramSubline] = useState("");
  const [simpleVipRecommendedBadge, setSimpleVipRecommendedBadge] = useState("");
  const [simpleMembershipVideoCta, setSimpleMembershipVideoCta] = useState("");
  const [simpleVipTapHint, setSimpleVipTapHint] = useState("");
  const [simpleTelegramActivationHint, setSimpleTelegramActivationHint] = useState("");
  const [simpleFooterTrustLine, setSimpleFooterTrustLine] = useState("");
  const [updatedAt, setUpdatedAt] = useState<string | null>(null);
  const [savedFingerprint, setSavedFingerprint] = useState<string | null>(null);
  const [activeLang, setActiveLang] = useState<ActiveLang>("es");
  const [simpleTextsByLang, setSimpleTextsByLang] = useState<Record<string, Record<string, string>>>({});
  const [aiTranslating, setAiTranslating] = useState(false);
  const [simpleMuxOverlayEnabled, setSimpleMuxOverlayEnabled] = useState(false);
  const [simpleMuxOverlayPlaybackId, setSimpleMuxOverlayPlaybackId] = useState("");
  const [vipPriceUsd, setVipPriceUsd] = useState("");
  /** CTA secundario "Acceso gratis" en /invitacion-exclusive (registro → aprobación manual). */
  const [freeAccessCtaEnabled, setFreeAccessCtaEnabled] = useState(false);
  const [freeAccessCtaLabel, setFreeAccessCtaLabel] = useState("");
  const [vipPriceArs, setVipPriceArs] = useState("");
  // Precios — funnel /invitacion-premium
  const [standardPriceUsd, setStandardPriceUsd] = useState("");
  const [standardPriceArs, setStandardPriceArs] = useState("");
  const [telegramBasicPriceCents, setTelegramBasicPriceCents] = useState("");
  // Precios — otros surfaces
  const [arInvitacionStandardPriceArs, setArInvitacionStandardPriceArs] = useState("");
  const [arInvitacionVipPriceArs, setArInvitacionVipPriceArs] = useState("");
  const [vipDonationSuggestedUsd, setVipDonationSuggestedUsd] = useState("");
  const [sorprendemeMinAmount, setSorprendemeMinAmount] = useState("");
  const [sorprendemeAmountTiers, setSorprendemeAmountTiers] = useState("");

  const location = useLocation();

  function hashToSection(hash: string): string | null {
    if (hash === "#hero") return "hero";
    if (hash === "#free-telegram") return "free-telegram";
    if (hash === "#cards" || hash === "#cards-bio-premium-simple") return "cards";
    if (hash === "#extra-texts") return "extra-texts";
    if (hash === "#layout-reels") return "layout-reels";
    if (hash === "#embudo") return "embudo";
    return null;
  }

  const SECTION_DOM_ID: Record<string, string> = {
    hero: "hero-bio-premium-simple",
    "free-telegram": "free-telegram-bio-premium-simple",
    cards: "cards-bio-premium-simple",
    "extra-texts": "extra-texts-bio-premium-simple",
    "layout-reels": "layout-reels",
    embudo: "embudo",
  };

  const [openSections, setOpenSections] = useState<Set<string>>(
    () => {
      const section = hashToSection(location.hash);
      return new Set(section ? [section] : ["hero", "free-telegram", "cards", "extra-texts", "layout-reels", "embudo"]);
    },
  );

  useEffect(() => {
    const section = hashToSection(location.hash);
    if (!section) return;
    setOpenSections(new Set([section]));
    // Smooth scroll a la sección abierta (esperar al próximo paint para que el contenido esté montado)
    const domId = SECTION_DOM_ID[section];
    if (!domId) return;
    const tryScroll = () => {
      const el = document.getElementById(domId);
      if (!el) return false;
      el.scrollIntoView({ behavior: "smooth", block: "start" });
      return true;
    };
    if (!tryScroll()) {
      requestAnimationFrame(() => { tryScroll(); });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- SECTION_DOM_ID is stable
  }, [location.hash]);

  const toggleSection = useCallback((id: string) => {
    setOpenSections(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  const applyConfig = useCallback((c: PremiumInvitePublicConfig) => {
    setExperimentEnabled(c.experiment_enabled);
    setUsePosthogLayout(c.use_posthog_layout);
    setForceLayout(
      c.force_layout === "premium_simple" || c.force_layout === "reels_feed"
        ? c.force_layout
        : "auto",
    );
    setReelsRollout(c.reels_rollout_percent);
    setTeaserCount(c.reels_teaser_count);
    setPlayerVariant(c.reels_player_variant);
    setReelsPlaybackSource(c.reels_playback_source);
    setCuratedPlaybackText((c.reels_curated_playback_ids ?? []).join("\n"));
    setFreeTelegramUrl(c.free_telegram_url ?? "");
    setFreeTelegramUrlByCountry(c.free_telegram_url_by_country ?? {});
    setFreeTelegramEnabled(c.free_telegram_enabled ?? true);
    setFreeTelegramByCountryEditor(formatFreeTelegramByCountry(c.free_telegram_url_by_country ?? {}));
    setDualCtaFreeChannelLabel(c.dual_cta_free_channel_label ?? "");
    setVipBaseUrl(c.vip_checkout_base_url ?? "");
    setEndHeadline(c.end_screen_headline ?? "");
    setEndFreeLbl(c.end_screen_free_cta_label ?? "");
    setEndVipLbl(c.end_screen_vip_cta_label ?? "");
    setMuxFriendlyBanner(muxTriFromConfig(c.mux_cta_friendly_banner));
    setMuxFreeJoinCopy(muxTriFromConfig(c.mux_cta_free_join_copy));
    setFriendlyBadgeText(c.friendly_vip_badge_text ?? "");
    setFriendlyDisclaimerText(c.friendly_vip_disclaimer_text ?? "");
    setSimpleShowTelegramCard(muxTriFromConfig(c.simple_show_telegram_card));
    setSimpleShowVipCard(muxTriFromConfig(c.simple_show_vip_card));
    setDualCtaFreeTelegram(muxTriFromConfig(c.dual_cta_free_telegram_enabled));
    setSimpleTelegramCardLocked(Boolean(c.simple_telegram_card_locked));
    setSimpleVipCardLocked(Boolean(c.simple_vip_card_locked));
    setSimpleTelegramCardTitle(c.simple_telegram_card_title ?? "");
    setSimpleTelegramCardCta(c.simple_telegram_card_cta ?? "");
    setSimpleVipCardTitle(c.simple_vip_card_title ?? "");
    setSimpleVipCardSubtitle(c.simple_vip_card_subtitle ?? "");
    setSimpleVipCardCta(c.simple_vip_card_cta ?? "");
    setSimpleHeroBadge(c.simple_hero_badge ?? "");
    setSimpleHeroTitle(c.simple_hero_title ?? "");
    setSimpleHeroSubtitle(c.simple_hero_subtitle ?? "");
    setSimpleHeroTrustLine(c.simple_hero_trust_line ?? "");
    setSimpleHeroPathLine(c.simple_hero_path_line ?? "");
    setSimpleFreeTelegramCta(c.simple_free_telegram_cta ?? "");
    setSimpleFreeTelegramBadge(c.simple_free_telegram_badge ?? "");
    setSimpleFreeTelegramIntro(c.simple_free_telegram_intro ?? "");
    setSimpleFreeTelegramSubline(c.simple_free_telegram_subline ?? "");
    setSimpleVipRecommendedBadge(c.simple_vip_recommended_badge ?? "");
    setSimpleMembershipVideoCta(c.simple_membership_video_cta ?? "");
    setSimpleVipTapHint(c.simple_vip_tap_hint ?? "");
    setSimpleTelegramActivationHint(c.simple_telegram_activation_hint ?? "");
    setSimpleFooterTrustLine(c.simple_footer_trust_line ?? "");
    if (c.simple_texts_by_lang) {
      try {
        setSimpleTextsByLang(JSON.parse(c.simple_texts_by_lang) as Record<string, Record<string, string>>);
      } catch { setSimpleTextsByLang({}); }
    } else {
      setSimpleTextsByLang({});
    }
    setSimpleMuxOverlayEnabled(Boolean(c.simple_mux_overlay_enabled));
    setSimpleMuxOverlayPlaybackId(c.simple_mux_overlay_playback_id ?? "");
    setVipPriceUsd(c.vip_price_usd != null ? String(c.vip_price_usd) : "");
    setVipPriceArs(c.vip_price_ars != null ? String(c.vip_price_ars) : "");
    setStandardPriceUsd(c.standard_price_usd != null ? String(c.standard_price_usd) : "");
    setStandardPriceArs(c.standard_price_ars != null ? String(c.standard_price_ars) : "");
    setTelegramBasicPriceCents(c.telegram_basic_price_cents != null ? String(c.telegram_basic_price_cents) : "");
    setArInvitacionStandardPriceArs(c.ar_invitacion_standard_price_ars != null ? String(c.ar_invitacion_standard_price_ars) : "");
    setArInvitacionVipPriceArs(c.ar_invitacion_vip_price_ars != null ? String(c.ar_invitacion_vip_price_ars) : "");
    setVipDonationSuggestedUsd(c.vip_donation_suggested_usd != null ? String(c.vip_donation_suggested_usd) : "");
    setSorprendemeMinAmount(c.sorprendeme_min_amount != null ? String(c.sorprendeme_min_amount) : "");
    setSorprendemeAmountTiers(c.sorprendeme_amount_tiers != null ? c.sorprendeme_amount_tiers : "");
    setFreeAccessCtaEnabled(c.free_access_cta_enabled === true);
    setFreeAccessCtaLabel(c.free_access_cta_label ?? "");
    setUpdatedAt(c.updated_at);
    setSavedFingerprint(configFingerprint(c));
  }, []);

  const formFingerprint = useMemo(
    () =>
      buildFormFingerprint({
        experimentEnabled,
        usePosthogLayout,
        forceLayout,
        reelsRollout,
        teaserCount,
        playerVariant,
        reelsPlaybackSource,
        curatedPlaybackText,
        freeTelegramUrl,
        freeTelegramUrlByCountry,
        freeTelegramByCountryEditor,
        freeTelegramEnabled,
        dualCtaFreeChannelLabel,
        vipBaseUrl,
        endHeadline,
        endFreeLbl,
        endVipLbl,
        muxFriendlyBanner,
        muxFreeJoinCopy,
        friendlyBadgeText,
        friendlyDisclaimerText,
        simpleShowTelegramCard,
        simpleShowVipCard,
        dualCtaFreeTelegram,
        simpleTelegramCardLocked,
        simpleVipCardLocked,
        simpleTelegramCardTitle,
        simpleTelegramCardCta,
        simpleVipCardTitle,
        simpleVipCardSubtitle,
        simpleVipCardCta,
        simpleHeroBadge,
        simpleHeroTitle,
        simpleHeroSubtitle,
        simpleHeroTrustLine,
        simpleHeroPathLine,
        simpleFreeTelegramCta,
        simpleFreeTelegramBadge,
        simpleFreeTelegramIntro,
        simpleFreeTelegramSubline,
        simpleVipRecommendedBadge,
        simpleMembershipVideoCta,
        simpleVipTapHint,
        simpleTelegramActivationHint,
        simpleFooterTrustLine,
        simpleTextsByLang,
        simpleMuxOverlayEnabled,
        simpleMuxOverlayPlaybackId,
        vipPriceUsd,
        vipPriceArs,
        standardPriceUsd,
        standardPriceArs,
        telegramBasicPriceCents,
        arInvitacionStandardPriceArs,
        arInvitacionVipPriceArs,
        vipDonationSuggestedUsd,
        sorprendemeMinAmount,
        sorprendemeAmountTiers,
        freeAccessCtaEnabled,
        freeAccessCtaLabel,
      }),
    [
      experimentEnabled,
      usePosthogLayout,
      forceLayout,
      reelsRollout,
      teaserCount,
      playerVariant,
      reelsPlaybackSource,
      curatedPlaybackText,
      freeTelegramUrl,
      freeTelegramUrlByCountry,
      freeTelegramByCountryEditor,
      freeTelegramEnabled,
      dualCtaFreeChannelLabel,
      vipBaseUrl,
      endHeadline,
      endFreeLbl,
      endVipLbl,
      muxFriendlyBanner,
      muxFreeJoinCopy,
      friendlyBadgeText,
      friendlyDisclaimerText,
      simpleShowTelegramCard,
      simpleShowVipCard,
      dualCtaFreeTelegram,
      simpleTelegramCardLocked,
      simpleVipCardLocked,
      simpleTelegramCardTitle,
      simpleTelegramCardCta,
      simpleVipCardTitle,
      simpleVipCardSubtitle,
      simpleVipCardCta,
      simpleHeroBadge,
      simpleHeroTitle,
      simpleHeroSubtitle,
      simpleHeroTrustLine,
      simpleHeroPathLine,
      simpleFreeTelegramCta,
      simpleFreeTelegramBadge,
      simpleFreeTelegramIntro,
      simpleFreeTelegramSubline,
      simpleVipRecommendedBadge,
      simpleMembershipVideoCta,
      simpleVipTapHint,
      simpleTelegramActivationHint,
      simpleFooterTrustLine,
      simpleTextsByLang,
      simpleMuxOverlayEnabled,
      simpleMuxOverlayPlaybackId,
      vipPriceUsd,
      vipPriceArs,
      standardPriceUsd,
      standardPriceArs,
      telegramBasicPriceCents,
      arInvitacionStandardPriceArs,
      arInvitacionVipPriceArs,
      vipDonationSuggestedUsd,
      sorprendemeMinAmount,
      sorprendemeAmountTiers,
      freeAccessCtaEnabled,
      freeAccessCtaLabel,
    ],
  );

  const isDirty = savedFingerprint !== null && formFingerprint !== savedFingerprint;
  const isDirtyRef = useRef(false);
  useEffect(() => {
    isDirtyRef.current = isDirty;
  }, [isDirty]);

  const curatedSummary = useMemo(
    () =>
      summarizeCuratedPlaybackEditor(
        reelsPlaybackSource === "curated" ? curatedPlaybackText : "",
      ),
    [reelsPlaybackSource, curatedPlaybackText],
  );

  // ── Per-language text helpers ────────────────────────────────────────────
  function getLT(field: string): string {
    if (activeLang === "es") {
      const esMap: Record<string, string> = {
        badge: simpleHeroBadge,
        heroTitle: simpleHeroTitle,
        heroSubtitle: simpleHeroSubtitle,
        heroTrustLine: simpleHeroTrustLine,
        heroPathLine: simpleHeroPathLine,
        freeTelegramSectionIntro: simpleFreeTelegramIntro,
        freeTelegramBadge: simpleFreeTelegramBadge,
        freeTelegramCta: simpleFreeTelegramCta,
        freeTelegramSubline: simpleFreeTelegramSubline,
        telegramCardTitle: simpleTelegramCardTitle,
        telegramCta: simpleTelegramCardCta,
        membershipCardTitle: simpleVipCardTitle,
        membershipCardSubtitle: simpleVipCardSubtitle,
        membershipCta: simpleVipCardCta,
        vipRecommendedBadge: simpleVipRecommendedBadge,
        membershipVideoCta: simpleMembershipVideoCta,
        vipTapHint: simpleVipTapHint,
        telegramActivationHint: simpleTelegramActivationHint,
        footerTrustLine: simpleFooterTrustLine,
      };
      return esMap[field] ?? "";
    }
    return simpleTextsByLang[activeLang]?.[field] ?? "";
  }

  function setLT(field: string, value: string): void {
    if (activeLang === "es") {
      const esSetters: Record<string, (v: string) => void> = {
        badge: setSimpleHeroBadge,
        heroTitle: setSimpleHeroTitle,
        heroSubtitle: setSimpleHeroSubtitle,
        heroTrustLine: setSimpleHeroTrustLine,
        heroPathLine: setSimpleHeroPathLine,
        freeTelegramSectionIntro: setSimpleFreeTelegramIntro,
        freeTelegramBadge: setSimpleFreeTelegramBadge,
        freeTelegramCta: setSimpleFreeTelegramCta,
        freeTelegramSubline: setSimpleFreeTelegramSubline,
        telegramCardTitle: setSimpleTelegramCardTitle,
        telegramCta: setSimpleTelegramCardCta,
        membershipCardTitle: setSimpleVipCardTitle,
        membershipCardSubtitle: setSimpleVipCardSubtitle,
        membershipCta: setSimpleVipCardCta,
        vipRecommendedBadge: setSimpleVipRecommendedBadge,
        membershipVideoCta: setSimpleMembershipVideoCta,
        vipTapHint: setSimpleVipTapHint,
        telegramActivationHint: setSimpleTelegramActivationHint,
        footerTrustLine: setSimpleFooterTrustLine,
      };
      esSetters[field]?.(value);
      return;
    }
    setSimpleTextsByLang((prev) => ({
      ...prev,
      [activeLang]: { ...(prev[activeLang] ?? {}), [field]: value },
    }));
  }

  const generateWithAI = useCallback(async () => {
    setAiTranslating(true);
    try {
      const esTexts: Record<string, string> = {
        badge: simpleHeroBadge,
        heroTitle: simpleHeroTitle,
        heroSubtitle: simpleHeroSubtitle,
        heroTrustLine: simpleHeroTrustLine,
        heroPathLine: simpleHeroPathLine,
        freeTelegramSectionIntro: simpleFreeTelegramIntro,
        freeTelegramBadge: simpleFreeTelegramBadge,
        freeTelegramCta: simpleFreeTelegramCta,
        freeTelegramSubline: simpleFreeTelegramSubline,
        telegramCardTitle: simpleTelegramCardTitle,
        telegramCta: simpleTelegramCardCta,
        membershipCardTitle: simpleVipCardTitle,
        membershipCardSubtitle: simpleVipCardSubtitle,
        membershipCta: simpleVipCardCta,
        vipRecommendedBadge: simpleVipRecommendedBadge,
        membershipVideoCta: simpleMembershipVideoCta,
        vipTapHint: simpleVipTapHint,
        telegramActivationHint: simpleTelegramActivationHint,
        footerTrustLine: simpleFooterTrustLine,
      };
      const headers = await getAuthHeaders();
      const res = await fetch("/api/admin/premium-invite-ai-translate", {
        method: "POST",
        headers,
        body: JSON.stringify({ es: esTexts, targetLangs: ["en", "pt", "fr", "it"] }),
      });
      const data = await res.json() as { translations?: Record<string, Record<string, string>>; error?: string };
      if (!res.ok) throw new Error(data.error ?? `Error ${res.status}`);
      if (data.translations) {
        setSimpleTextsByLang((prev) => ({ ...prev, ...data.translations }));
        toast({ title: "Traducciones generadas", description: "Revisá cada idioma (EN, PT, FR, IT) y guardá cuando estés conforme." });
      }
    } catch (e) {
      toast({ title: "Error al generar", description: e instanceof Error ? e.message : "Error", variant: "destructive" });
    } finally {
      setAiTranslating(false);
    }
  }, [
    getAuthHeaders,
    simpleHeroBadge, simpleHeroTitle, simpleHeroSubtitle, simpleHeroTrustLine, simpleHeroPathLine,
    simpleFreeTelegramIntro, simpleFreeTelegramBadge, simpleFreeTelegramCta, simpleFreeTelegramSubline,
    simpleTelegramCardTitle, simpleTelegramCardCta,
    simpleVipCardTitle, simpleVipCardSubtitle, simpleVipCardCta,
    simpleVipRecommendedBadge, simpleMembershipVideoCta, simpleVipTapHint,
    simpleTelegramActivationHint, simpleFooterTrustLine,
    toast,
  ]);

  useEffect(() => {
    if (!isDirty) return;
    const onBeforeUnload = (e: BeforeUnloadEvent) => {
      e.preventDefault();
    };
    window.addEventListener("beforeunload", onBeforeUnload);
    return () => window.removeEventListener("beforeunload", onBeforeUnload);
  }, [isDirty]);

  const load = useCallback(
    async (opts?: { silent?: boolean }) => {
      if (!isLoaded) return;
      if (opts?.silent && isDirtyRef.current) {
        if (
          !window.confirm("¿Descartar los cambios sin guardar y recargar desde el servidor?")
        ) {
          return;
        }
      }
      if (opts?.silent) setRefetching(true);
      else setLoading(true);
      try {
        const headers = await getAuthHeaders();
        const res = await fetch("/api/admin/premium-invite-config", { headers });
        const data = await readJsonResponse(res);
        if (!res.ok) {
          if (res.status === 401) {
            throw new Error(
              "No autorizado: necesitás sesión de admin en Clerk (role admin).",
            );
          }
          const msg =
            data !== null && typeof data === "object" && "error" in data
              ? String((data as { error?: unknown }).error ?? res.statusText)
              : res.statusText;
          throw new Error(msg);
        }
        const cfg = parseAdminResponse(data);
        if (!cfg) throw new Error("Respuesta del servidor inválida");
        applyConfig(cfg);
      } catch (e) {
        toast({
          title: "No se pudo cargar",
          description: e instanceof Error ? e.message : "Error desconocido",
          variant: "destructive",
        });
      } finally {
        if (opts?.silent) setRefetching(false);
        else setLoading(false);
      }
    },
    [getAuthHeaders, isLoaded, applyConfig, toast],
  );

  useEffect(() => {
    void load();
  }, [load]);

  const save = useCallback(async () => {
    if (reelsPlaybackSource === "curated" && curatedSummary.validIds.length === 0) {
      toast({
        title: "Lista curada vacía",
        description:
          "Agregá al menos un playback ID Mux válido (uno por línea) o elegí \"Automático (Mux API)\".",
        variant: "destructive",
      });
      return;
    }

    setSaving(true);
    try {
      const headers = await getAuthHeaders();
      const curatedIds =
        reelsPlaybackSource === "curated"
          ? parseMuxPlaybackIdsFromEditorText(curatedPlaybackText)
          : [];
      const res = await fetch("/api/admin/premium-invite-config", {
        method: "POST",
        headers,
        body: JSON.stringify({
          experiment_enabled: experimentEnabled,
          use_posthog_layout: usePosthogLayout,
          force_layout: forceLayout === "auto" ? null : forceLayout,
          reels_rollout_percent: reelsRollout,
          reels_teaser_count: teaserCount,
          reels_player_variant: playerVariant,
          reels_playback_source: reelsPlaybackSource,
          reels_curated_playback_ids: curatedIds,
          free_telegram_url: freeTelegramUrl.trim() || null,
          free_telegram_url_by_country: parseFreeTelegramByCountryEditor(freeTelegramByCountryEditor),
          free_telegram_enabled: freeTelegramEnabled,
          vip_checkout_base_url: vipBaseUrl.trim() || null,
          end_screen_headline: endHeadline.trim() || null,
          end_screen_free_cta_label: endFreeLbl.trim() || null,
          end_screen_vip_cta_label: endVipLbl.trim() || null,
          mux_cta_friendly_banner: muxTriToPayload(muxFriendlyBanner),
          mux_cta_free_join_copy: muxTriToPayload(muxFreeJoinCopy),
          friendly_vip_badge_text: friendlyBadgeText.trim() || null,
          friendly_vip_disclaimer_text: friendlyDisclaimerText.trim() || null,
          simple_show_telegram_card: muxTriToPayload(simpleShowTelegramCard),
          simple_show_vip_card: muxTriToPayload(simpleShowVipCard),
          dual_cta_free_telegram_enabled: muxTriToPayload(dualCtaFreeTelegram),
          simple_telegram_card_locked: simpleTelegramCardLocked,
          simple_vip_card_locked: simpleVipCardLocked,
          simple_telegram_card_title: simpleTelegramCardTitle.trim() || null,
          simple_telegram_card_cta: simpleTelegramCardCta.trim() || null,
          dual_cta_free_channel_label: dualCtaFreeChannelLabel.trim() || null,
          simple_vip_card_title: simpleVipCardTitle.trim() || null,
          simple_vip_card_subtitle: simpleVipCardSubtitle.trim() || null,
          simple_vip_card_cta: simpleVipCardCta.trim() || null,
          simple_hero_badge: simpleHeroBadge.trim() || null,
          simple_hero_title: simpleHeroTitle.trim() || null,
          simple_hero_subtitle: simpleHeroSubtitle.trim() || null,
          simple_hero_trust_line: simpleHeroTrustLine.trim() || null,
          simple_hero_path_line: simpleHeroPathLine.trim() || null,
          simple_free_telegram_cta: simpleFreeTelegramCta.trim() || null,
          simple_free_telegram_badge: simpleFreeTelegramBadge.trim() || null,
          simple_free_telegram_intro: simpleFreeTelegramIntro.trim() || null,
          simple_free_telegram_subline: simpleFreeTelegramSubline.trim() || null,
          simple_vip_recommended_badge: simpleVipRecommendedBadge.trim() || null,
          simple_membership_video_cta: simpleMembershipVideoCta.trim() || null,
          simple_vip_tap_hint: simpleVipTapHint.trim() || null,
          simple_telegram_activation_hint: simpleTelegramActivationHint.trim() || null,
          simple_footer_trust_line: simpleFooterTrustLine.trim() || null,
          simple_texts_by_lang: Object.keys(simpleTextsByLang).length > 0 ? JSON.stringify(simpleTextsByLang) : null,
          simple_mux_overlay_enabled: simpleMuxOverlayEnabled,
          simple_mux_overlay_playback_id: simpleMuxOverlayPlaybackId.trim() || null,
          vip_price_usd: (() => { const n = parseFloat(vipPriceUsd); return Number.isFinite(n) && n > 0 ? n : null; })(),
          vip_price_ars: (() => { const n = parseFloat(vipPriceArs); return Number.isFinite(n) && n > 0 ? n : null; })(),
          standard_price_usd: (() => { const n = parseFloat(standardPriceUsd); return Number.isFinite(n) && n > 0 ? n : null; })(),
          standard_price_ars: (() => { const n = parseFloat(standardPriceArs); return Number.isFinite(n) && n > 0 ? n : null; })(),
          telegram_basic_price_cents: (() => { const n = parseFloat(telegramBasicPriceCents); return Number.isFinite(n) && n > 0 ? n : null; })(),
          ar_invitacion_standard_price_ars: (() => { const n = parseFloat(arInvitacionStandardPriceArs); return Number.isFinite(n) && n > 0 ? n : null; })(),
          ar_invitacion_vip_price_ars: (() => { const n = parseFloat(arInvitacionVipPriceArs); return Number.isFinite(n) && n > 0 ? n : null; })(),
          vip_donation_suggested_usd: (() => { const n = parseFloat(vipDonationSuggestedUsd); return Number.isFinite(n) && n > 0 ? n : null; })(),
          sorprendeme_min_amount: (() => { const n = parseFloat(sorprendemeMinAmount); return Number.isFinite(n) && n > 0 ? n : null; })(),
          sorprendeme_amount_tiers: sorprendemeAmountTiers.trim() || null,
          free_access_cta_enabled: freeAccessCtaEnabled,
          free_access_cta_label: freeAccessCtaLabel.trim() || null,
        }),
      });
      const data = await readJsonResponse(res);
      if (!res.ok) {
        if (res.status === 401) {
          throw new Error(
            "No autorizado: necesitás sesión de admin en Clerk (role admin).",
          );
        }
        const msg =
          data !== null && typeof data === "object" && "error" in data
            ? String((data as { error?: unknown }).error ?? res.statusText)
            : res.statusText;
        throw new Error(msg);
      }
      const cfg = parseAdminResponse(data);
      if (!cfg) throw new Error("Respuesta inválida al guardar");
      applyConfig(cfg);
      // Cross-tab (storage) + same-origin listeners (CustomEvent) → DualCTA soft-refetch ya.
      broadcastPremiumInviteConfigUpdated();
      toast({
        title: "Guardado",
        description:
          "Guardado en DB + broadcast. Otras pestañas del sitio: al instante (storage) o al volver a la pestaña. Poll de respaldo ≤20s. Mux global: nuevas visitas o remount (override por país pisa fallback).",
      });
    } catch (e) {
      toast({
        title: "Error al guardar",
        description: e instanceof Error ? e.message : "Error desconocido",
        variant: "destructive",
      });
    } finally {
      setSaving(false);
    }
  }, [
    applyConfig,
    curatedPlaybackText,
    curatedSummary.validIds.length,
    endFreeLbl,
    endHeadline,
    endVipLbl,
    experimentEnabled,
    forceLayout,
    freeTelegramUrl,
    freeTelegramByCountryEditor,
    freeTelegramEnabled,
    dualCtaFreeChannelLabel,
    friendlyBadgeText,
    friendlyDisclaimerText,
    getAuthHeaders,
    muxFreeJoinCopy,
    muxFriendlyBanner,
    playerVariant,
    reelsPlaybackSource,
    reelsRollout,
    simpleShowTelegramCard,
    simpleShowVipCard,
    dualCtaFreeTelegram,
    simpleTelegramCardLocked,
    simpleVipCardLocked,
    simpleTelegramCardTitle,
    simpleTelegramCardCta,
    simpleVipCardTitle,
    simpleVipCardSubtitle,
    simpleVipCardCta,
    teaserCount,
    toast,
    usePosthogLayout,
    vipBaseUrl,
    simpleTextsByLang,
    simpleMuxOverlayEnabled,
    simpleMuxOverlayPlaybackId,
    vipPriceUsd,
    vipPriceArs,
    standardPriceUsd,
    standardPriceArs,
    telegramBasicPriceCents,
    arInvitacionStandardPriceArs,
    arInvitacionVipPriceArs,
    vipDonationSuggestedUsd,
    sorprendemeMinAmount,
    sorprendemeAmountTiers,
    freeAccessCtaEnabled,
    freeAccessCtaLabel,
  ]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (!(e.metaKey || e.ctrlKey) || e.key !== "s") return;
      e.preventDefault();
      if (loading || saving || refetching) return;
      void save();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [loading, saving, refetching, save]);

  if (loading) {
    return (
      <div className="flex min-h-[40vh] flex-col items-center justify-center gap-2 text-muted-foreground">
        <Loader2 className="h-8 w-8 animate-spin" aria-hidden />
        <span className="sr-only">Cargando configuración</span>
      </div>
    );
  }

  return (
    <AdminPageShell width="lg">
      <AdminPageHeader
        description={
          <>
            Fallbacks globales (free TG, reels, mux overlay). DualCTA por país →{" "}
            <strong>Home por país</strong> (dueño). Códigos one-time → <strong>Códigos invite</strong>.
            {isDirty && (
              <span className="ml-2 rounded-full border border-amber-500/40 bg-amber-950/40 px-2 py-0.5 text-xs font-medium text-amber-100">
                Sin guardar
              </span>
            )}
          </>
        }
        actions={
          <div className="flex flex-col items-end gap-1 text-xs text-muted-foreground">
            <p>
              Actualizado:{" "}
              <strong className="text-foreground">
                {formatServerDate(updatedAt) ?? updatedAt ?? "—"}
              </strong>
            </p>
            <div className="flex flex-wrap gap-x-3 gap-y-1">
              <span className="text-muted-foreground/60">Probar:</span>
              <a
                href="/invitacion-premium?invite_layout=reels_feed"
                target="_blank"
                rel="noopener noreferrer"
                className="text-sky-400 underline-offset-4 hover:underline"
              >
                layout reels
              </a>
              <a
                href="/invitacion-premium?invite_layout=premium_simple"
                target="_blank"
                rel="noopener noreferrer"
                className="text-sky-400 underline-offset-4 hover:underline"
              >
                layout simple
              </a>
              <a
                href={`/invitacion-premium?invite_layout=premium_simple&preview_show_vip=${simpleShowVipCard === "off" ? "0" : "1"}&preview_show_tg=${simpleShowTelegramCard === "off" ? "0" : "1"}`}
                target="_blank"
                rel="noopener noreferrer"
                className="text-amber-400 underline-offset-4 hover:underline"
              >
                cards actuales ↗
              </a>
            </div>
          </div>
        }
      />

      <ConfigOwnerCallout
        className="mb-4"
        variant="fallback"
        owns="premium_invite_config: free TG URL, reels, simple_mux_overlay, vip_price_usd (display)."
        notHere="Mux / VIP price_ / promo_ / plantilla por ISO2 — no se editan acá."
        ownerPath="/admin/country-templates"
        ownerLabel="Home por país (dueño DualCTA)"
        priority={[
          "Mux home: country mux (A/B) gana sobre simple_mux_overlay de esta page",
          "Cobro DualCTA: country stripePriceId gana sobre vip_price_usd (solo copy)",
          "Free TG URL: country map invite → URL global invite → env",
        ]}
      />

      {/* ── Surface legend ─────────────────────────────────────────────────── */}
      <div className="mb-4 flex flex-wrap items-center gap-x-4 gap-y-2 rounded-2xl border border-white/[0.06] bg-zinc-900/30 px-4 py-3">
        <span className="text-[11px] font-medium text-muted-foreground/80">Superficies:</span>
        <span className="flex items-center gap-1.5"><SurfaceBadge surface="simple" /><span className="text-[11px] text-muted-foreground">/invitacion-premium (layout simple)</span></span>
        <span className="flex items-center gap-1.5"><SurfaceBadge surface="home" /><span className="text-[11px] text-muted-foreground">home del creator</span></span>
        <span className="flex items-center gap-1.5"><SurfaceBadge surface="reels" /><span className="text-[11px] text-muted-foreground">/invitacion-premium (feed Reels)</span></span>
        <span className="flex items-center gap-1.5"><SurfaceBadge surface="overlay" /><span className="text-[11px] text-muted-foreground">video fullscreen al cargar</span></span>
      </div>

      {/* ── Checklist de activación ────────────────────────────────────────── */}
      {(() => {
        const effectiveFreeTelegram = freeTelegramUrl.trim() || EFFECTIVE_FREE_TELEGRAM_URL;
        const effectiveVipBase = vipBaseUrl.trim() || EFFECTIVE_VIP_BASE_URL;

        type CheckStatus = "ok" | "warn" | "error";
        interface CheckItem {
          key: string;
          label: string;
          description: string;
          fix: string;
          status: CheckStatus;
        }

        const items: CheckItem[] = [
          {
            key: "vip_mux",
            label: "Video VIP (Mux)",
            description: "Video que se reproduce al abrir la card Mi VIP.",
            fix: "Configurar VITE_PREMIUM_MUX_ES_VIP o VITE_PREMIUM_SIMPLE_MUX_PLAYBACK_ID en Railway → redeploy.",
            status: TEMPLATE_PREREQS.hasVipMux ? "ok" : "error",
          },
          {
            key: "telegram_private",
            label: "Telegram privado",
            description: "URL de invite para la card Grupo privado (post-pago).",
            fix: "Configurar VITE_TELEGRAM_PRIVATE_GROUP_URL en Railway.",
            status: TEMPLATE_PREREQS.hasTelegramPrivateUrl ? "ok" : "error",
          },
          {
            key: "vip_checkout",
            label: "Checkout VIP",
            description: "Destino del botón \"Ir al VIP\" (base URL del checkout).",
            fix: "Completar Base checkout VIP en la sección → URLs, funnel y pantalla final.",
            status: effectiveVipBase ? "ok" : "warn",
          },
          {
            key: "free_telegram",
            label: "Canal Telegram gratis",
            description: "Enlace al canal público (opcional). Sin este, el botón verde no aparece.",
            fix: "Completar Telegram público gratis en → URLs, funnel y pantalla final, o VITE_PREMIUM_SIMPLE_FREE_TELEGRAM_URL en Railway.",
            status: effectiveFreeTelegram ? "ok" : "warn",
          },
          {
            key: "mp_ar",
            label: "MercadoPago AR",
            description: "Checkout MP para usuarios de Argentina. Sin esto usan Stripe USD.",
            fix: "Configurar VITE_MP_CHECKOUT_BASIC_AR en Railway (URL completa de preferencia MP).",
            status: TEMPLATE_PREREQS.hasMpCheckoutAr ? "ok" : "warn",
          },
          ...(simpleMuxOverlayEnabled
            ? [
                {
                  key: "overlay_mux",
                  label: "Video del overlay",
                  description: "El overlay está activo pero no hay Mux ID para reproducir.",
                  fix: "Ingresar un Playback ID en el campo Overlay video (sección Cards de acción) o configurar VITE_PREMIUM_MUX_ES_VIP.",
                  status: (TEMPLATE_PREREQS.hasVipMux || simpleMuxOverlayPlaybackId.trim()) ? "ok" as CheckStatus : "error" as CheckStatus,
                },
              ]
            : []),
        ];

        const errorCount = items.filter((i) => i.status === "error").length;
        const warnCount = items.filter((i) => i.status === "warn").length;
        const allOk = errorCount === 0 && warnCount === 0;

        const headerBorderCls = allOk
          ? "border-emerald-700/30"
          : errorCount > 0
            ? "border-red-700/30"
            : "border-amber-700/30";
        const HeaderIcon = allOk ? CheckCircle2 : errorCount > 0 ? XCircle : AlertTriangle;
        const headerIconCls = allOk
          ? "text-emerald-400"
          : errorCount > 0
            ? "text-red-400"
            : "text-amber-400";

        return (
          <div className={cn("mb-4 overflow-hidden rounded-2xl border bg-zinc-900/50", headerBorderCls)}>
            <div className="flex flex-wrap items-center gap-3 px-4 py-3">
              <HeaderIcon className={cn("h-4 w-4 shrink-0", headerIconCls)} aria-hidden />
              <span className="text-sm font-semibold text-foreground">Checklist de activación</span>
              {allOk ? (
                <span className="text-[11px] text-emerald-400">Todo configurado · el template está listo</span>
              ) : (
                <span className="text-[11px] text-muted-foreground">
                  {errorCount > 0 && (
                    <span className="text-red-400">{errorCount} faltante{errorCount !== 1 ? "s" : ""}</span>
                  )}
                  {errorCount > 0 && warnCount > 0 && <span className="text-muted-foreground/60"> · </span>}
                  {warnCount > 0 && (
                    <span className="text-amber-400">{warnCount} opcional{warnCount !== 1 ? "es" : ""} sin config</span>
                  )}
                </span>
              )}
            </div>
            <div className="grid gap-2 border-t border-white/[0.06] px-4 pb-4 pt-3 sm:grid-cols-2 lg:grid-cols-3">
              {items.map((item) => {
                const ItemIcon = item.status === "ok" ? CheckCircle2 : item.status === "error" ? XCircle : AlertTriangle;
                const iconCls = item.status === "ok" ? "text-emerald-400" : item.status === "error" ? "text-red-400" : "text-amber-400";
                const cardCls = item.status === "ok"
                  ? "border-emerald-700/25 bg-emerald-950/15"
                  : item.status === "error"
                    ? "border-red-700/35 bg-red-950/20"
                    : "border-amber-700/25 bg-amber-950/15";
                const labelCls = item.status === "ok" ? "text-emerald-300" : item.status === "error" ? "text-red-300" : "text-amber-300";

                return (
                  <div key={item.key} className={cn("flex gap-2.5 rounded-xl border px-3 py-2.5", cardCls)}>
                    <ItemIcon className={cn("mt-0.5 h-3.5 w-3.5 shrink-0", iconCls)} aria-hidden />
                    <div className="min-w-0 space-y-0.5">
                      <p className={cn("text-xs font-semibold", labelCls)}>{item.label}</p>
                      <p className="text-[11px] leading-snug text-muted-foreground/80">{item.description}</p>
                      {item.status !== "ok" && (
                        <p className="text-[11px] leading-snug text-zinc-500">→ {item.fix}</p>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        );
      })()}

      {/* ── Accordion sections ─────────────────────────────────────────────── */}
      <div className="space-y-3">

        {/* ── Language Selector + AI ────────────────────────────────────── */}
        <div className="overflow-hidden rounded-2xl border border-white/[0.08] bg-zinc-900/50 px-5 py-4">
          <div className="flex flex-wrap items-center gap-2">
            <Languages className="h-4 w-4 shrink-0 text-violet-400" aria-hidden />
            <span className="text-sm font-medium text-zinc-300">Idioma de textos:</span>
            {(["es", "en", "pt", "fr", "it"] as const).map((l) => (
              <button
                key={l}
                type="button"
                onClick={() => setActiveLang(l)}
                className={cn(
                  "rounded-full px-3 py-1 text-xs font-semibold transition-colors",
                  activeLang === l
                    ? "bg-violet-600 text-white"
                    : "bg-zinc-800 text-zinc-400 hover:bg-zinc-700",
                )}
              >
                {LANG_LABELS[l]}
              </button>
            ))}
            <div className="flex-1" />
            <button
              type="button"
              onClick={() => { void generateWithAI(); }}
              disabled={aiTranslating}
              className="flex items-center gap-2 rounded-full border border-amber-500/30 bg-amber-500/10 px-4 py-1.5 text-xs font-semibold text-amber-300 transition-colors hover:bg-amber-500/20 disabled:opacity-50"
            >
              {aiTranslating ? (
                <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden />
              ) : (
                <Sparkles className="h-3.5 w-3.5" aria-hidden />
              )}
              {aiTranslating ? "Generando..." : "Generar con AI"}
            </button>
          </div>
          {activeLang !== "es" && (
            <p className="mt-2 text-xs text-zinc-500">
              Editando textos en <strong className="text-zinc-300">{LANG_LABELS[activeLang]}</strong>.
              Vacío = heredar el texto del código para ese idioma. Los textos en ES se aplican como fallback universal.
            </p>
          )}
        </div>

        {/* ── Section: Hero Bio Premium Simple ──────────────────────────── */}
        <div id="hero-bio-premium-simple" className="overflow-hidden rounded-2xl border border-white/[0.08] bg-zinc-900/50">
          <button
            type="button"
            onClick={() => toggleSection("hero")}
            className="flex w-full items-center gap-3 px-5 py-4 text-left transition-colors hover:bg-white/[0.03]"
          >
            <Type className="h-4 w-4 shrink-0 text-violet-400" aria-hidden />
            <div className="flex flex-1 min-w-0 flex-col gap-1">
              <div className="flex flex-wrap items-center gap-1.5">
                <span className="text-sm font-semibold text-foreground">Hero — título y textos de portada</span>
                <SurfaceBadge surface="simple" />
                <SurfaceBadge surface="home" />
              </div>
              <span className="text-[11px] text-muted-foreground">Badge, título principal, subtítulo, línea de confianza y pill de valor · aparecen en la parte superior de la página</span>
            </div>
            <ChevronDown className={cn("h-4 w-4 shrink-0 text-zinc-500 transition-transform duration-200", openSections.has("hero") && "rotate-180")} aria-hidden />
          </button>
          {openSections.has("hero") && (
            <div className="border-t border-white/[0.06] space-y-4 px-5 pb-5 pt-4">
              <p className="text-xs text-muted-foreground">
                Textos de la <strong className="text-muted-foreground">parte superior</strong> de la página, encima del enlace Telegram gratis y las cards.
                Aplica a <code className="text-[10px]">/invitacion-premium</code> (layout simple) y a la home del creator.{" "}
                Vacío = usar el texto por idioma del código.
              </p>
              <div className="rounded-2xl border border-white/[0.07] bg-zinc-800/30 p-5 space-y-4">
                <div className="space-y-1.5">
                  <label className="text-xs font-medium text-zinc-300">Badge pequeño (encima del título)</label>
                  <input type="text" value={getLT("badge")} onChange={(e) => setLT("badge", e.target.value)} maxLength={60} placeholder="Ej: Acceso exclusivo" className="w-full rounded-xl border border-white/10 bg-zinc-900 px-3 py-2 text-sm text-white placeholder:text-zinc-600 focus:outline-none focus:ring-2 focus:ring-violet-500/40" />
                </div>
                <div className="space-y-1.5">
                  <label className="text-xs font-medium text-zinc-300">Título principal</label>
                  <input type="text" value={getLT("heroTitle")} onChange={(e) => setLT("heroTitle", e.target.value)} maxLength={80} placeholder="Ej: Contenido exclusivo para mis fans" className="w-full rounded-xl border border-white/10 bg-zinc-900 px-3 py-2 text-sm text-white placeholder:text-zinc-600 focus:outline-none focus:ring-2 focus:ring-violet-500/40" />
                </div>
                <div className="space-y-1.5">
                  <label className="text-xs font-medium text-zinc-300">Subtítulo</label>
                  <textarea value={getLT("heroSubtitle")} onChange={(e) => setLT("heroSubtitle", e.target.value)} maxLength={200} rows={2} placeholder="Ej: Accedé a mi contenido más personal y exclusivo" className="w-full rounded-xl border border-white/10 bg-zinc-900 px-3 py-2 text-sm text-white placeholder:text-zinc-600 focus:outline-none focus:ring-2 focus:ring-violet-500/40 resize-none" />
                </div>
                <div className="space-y-1.5">
                  <label className="text-xs font-medium text-zinc-300">Línea de confianza</label>
                  <input type="text" value={getLT("heroTrustLine")} onChange={(e) => setLT("heroTrustLine", e.target.value)} maxLength={120} placeholder="Ej: +5.000 miembros activos" className="w-full rounded-xl border border-white/10 bg-zinc-900 px-3 py-2 text-sm text-white placeholder:text-zinc-600 focus:outline-none focus:ring-2 focus:ring-violet-500/40" />
                </div>
                <div className="space-y-1.5">
                  <label className="text-xs font-medium text-zinc-300">Pill de valor (destacado en caja)</label>
                  <input type="text" value={getLT("heroPathLine")} onChange={(e) => setLT("heroPathLine", e.target.value)} maxLength={140} placeholder="Ej: Fotos y videos exclusivos · Comunidad privada · Acceso directo" className="w-full rounded-xl border border-white/10 bg-zinc-900 px-3 py-2 text-sm text-white placeholder:text-zinc-600 focus:outline-none focus:ring-2 focus:ring-violet-500/40" />
                </div>
              </div>
            </div>
          )}
        </div>

        {/* ── Section: Canal Telegram Gratis ───────────────────────────── */}
        <div id="free-telegram-bio-premium-simple" className="overflow-hidden rounded-2xl border border-white/[0.08] bg-zinc-900/50">
          <button
            type="button"
            onClick={() => toggleSection("free-telegram")}
            className="flex w-full items-center gap-3 px-5 py-4 text-left transition-colors hover:bg-white/[0.03]"
          >
            <ExternalLink className="h-4 w-4 shrink-0 text-emerald-400" aria-hidden />
            <div className="flex flex-1 min-w-0 flex-col gap-1">
              <div className="flex flex-wrap items-center gap-1.5">
                <span className="text-sm font-semibold text-foreground">Canal Telegram Gratis</span>
                <SurfaceBadge surface="simple" />
                <SurfaceBadge surface="home" />
              </div>
              <span className="text-[11px] text-muted-foreground">Bloque verde con enlace al canal público · aparece entre el hero y las cards (solo si hay URL configurada)</span>
            </div>
            <ChevronDown className={cn("h-4 w-4 shrink-0 text-zinc-500 transition-transform duration-200", openSections.has("free-telegram") && "rotate-180")} aria-hidden />
          </button>
          {openSections.has("free-telegram") && (
            <div className="border-t border-white/[0.06] space-y-4 px-5 pb-5 pt-4">
              <p className="text-xs text-muted-foreground">
                Textos del bloque verde con enlace al canal público de Telegram.
                Aparece <strong className="text-muted-foreground">entre el hero y las cards</strong>, solo cuando hay URL configurada en{" "}
                <strong className="text-muted-foreground">URLs, funnel y pantalla final → Telegram público gratis</strong>.
              </p>
              <div className="rounded-2xl border border-white/[0.07] bg-zinc-800/30 p-5 space-y-4">
                <div className="space-y-1.5">
                  <label className="text-xs font-medium text-zinc-300">Intro (encima del enlace)</label>
                  <input type="text" value={getLT("freeTelegramSectionIntro")} onChange={(e) => setLT("freeTelegramSectionIntro", e.target.value)} maxLength={120} placeholder='Ej: "Primero conoceme gratis:"' className="w-full rounded-xl border border-white/10 bg-zinc-900 px-3 py-2 text-sm text-white placeholder:text-zinc-600 focus:outline-none focus:ring-2 focus:ring-emerald-500/40" />
                </div>
                <div className="space-y-1.5">
                  <label className="text-xs font-medium text-zinc-300">Badge del enlace</label>
                  <input type="text" value={getLT("freeTelegramBadge")} onChange={(e) => setLT("freeTelegramBadge", e.target.value)} maxLength={50} placeholder='Ej: "Gratis"' className="w-full rounded-xl border border-white/10 bg-zinc-900 px-3 py-2 text-sm text-white placeholder:text-zinc-600 focus:outline-none focus:ring-2 focus:ring-emerald-500/40" />
                </div>
                <div className="space-y-1.5">
                  <label className="text-xs font-medium text-zinc-300">Texto del CTA</label>
                  <input type="text" value={getLT("freeTelegramCta")} onChange={(e) => setLT("freeTelegramCta", e.target.value)} maxLength={60} placeholder='Ej: "Canal gratis" (default: "Telegram")' className="w-full rounded-xl border border-white/10 bg-zinc-900 px-3 py-2 text-sm text-white placeholder:text-zinc-600 focus:outline-none focus:ring-2 focus:ring-emerald-500/40" />
                </div>
                <div className="space-y-1.5">
                  <label className="text-xs font-medium text-zinc-300">Sublinea (debajo del enlace)</label>
                  <input type="text" value={getLT("freeTelegramSubline")} onChange={(e) => setLT("freeTelegramSubline", e.target.value)} maxLength={120} placeholder='Ej: "Sin costo, sin compromiso"' className="w-full rounded-xl border border-white/10 bg-zinc-900 px-3 py-2 text-sm text-white placeholder:text-zinc-600 focus:outline-none focus:ring-2 focus:ring-emerald-500/40" />
                </div>
              </div>
            </div>
          )}
        </div>

        {/* ── Section: Cards Bio Premium Simple ─────────────────────────── */}
        <div id="cards-bio-premium-simple" className="overflow-hidden rounded-2xl border border-white/[0.08] bg-zinc-900/50">
          <button
            type="button"
            onClick={() => toggleSection("cards")}
            className="flex w-full items-center gap-3 px-5 py-4 text-left transition-colors hover:bg-white/[0.03]"
          >
            <MessageCircle className="h-4 w-4 shrink-0 text-sky-400" aria-hidden />
            <div className="flex flex-1 min-w-0 flex-col gap-1">
              <div className="flex flex-wrap items-center gap-1.5">
                <span className="text-sm font-semibold text-foreground">Cards de acción</span>
                <SurfaceBadge surface="simple" />
                <SurfaceBadge surface="home" />
              </div>
              <span className="text-[11px] text-muted-foreground">Visibilidad, bloqueo y textos de las cards Telegram + VIP en la parte inferior de la página</span>
            </div>
            <ChevronDown className={cn("h-4 w-4 shrink-0 text-zinc-500 transition-transform duration-200", openSections.has("cards") && "rotate-180")} aria-hidden />
          </button>
          {openSections.has("cards") && (
            <div className="border-t border-white/[0.06] space-y-4 px-5 pb-5 pt-4">

              {(() => {
                const risks = analyzeAdminFunnelRisks({
                  simpleShowTelegramCard,
                  simpleShowVipCard,
                  freeTelegramUrl,
                });
                if (risks.length === 0) return null;
                return (
                  <div className="flex items-start gap-3 rounded-2xl border border-amber-500/30 bg-amber-950/30 p-4 text-xs text-amber-100">
                    <Info className="mt-0.5 h-4 w-4 shrink-0" aria-hidden />
                    <div className="space-y-1.5">
                      <p className="font-semibold text-amber-50">
                        {risks.length === 1 ? "1 riesgo detectado en el embudo" : `${risks.length} riesgos detectados en el embudo`}
                      </p>
                      <ul className="space-y-1">
                        {risks.map((r) => (
                          <li key={r.id}>
                            <strong>{r.label}.</strong>{" "}
                            <span className="text-amber-100/80">{r.detail}</span>
                          </li>
                        ))}
                      </ul>
                      <p className="pt-1 text-[11px] text-amber-100/70">
                        Estos riesgos se reportan a PostHog en runtime; corregilos antes de guardar para no perder conversiones.
                      </p>
                    </div>
                  </div>
                );
              })()}

              <div className="rounded-2xl border border-white/[0.07] bg-zinc-800/30 p-5 space-y-4">
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <div>
                    <h2 className="text-sm font-semibold text-foreground">Visibilidad y bloqueo</h2>
                    <p className="mt-0.5 text-xs text-muted-foreground">
                      Controla si las cards aparecen en la <strong className="text-muted-foreground">parte inferior de la página</strong> (debajo del enlace Telegram gratis).
                      Aplica a landing simple y home. <strong className="text-muted-foreground">Heredar</strong> usa env variables de entorno.
                    </p>
                  </div>
                  <a
                    href={`/invitacion-premium?invite_layout=premium_simple&preview_show_vip=${simpleShowVipCard === "off" ? "0" : "1"}&preview_show_tg=${simpleShowTelegramCard === "off" ? "0" : "1"}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="shrink-0 text-xs text-amber-400 underline-offset-4 hover:underline"
                  >
                    Previsualizar ↗
                  </a>
                </div>

                <div className="grid gap-3 sm:grid-cols-2">
                  <div className="space-y-1.5">
                    <Label>Card Telegram</Label>
                    <Select value={simpleShowTelegramCard} onValueChange={(v: MuxTriState) => setSimpleShowTelegramCard(v)}>
                      <SelectTrigger className="border-white/[0.08] bg-zinc-700/50"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="inherit">{inheritLabel(INHERITED_FROM_ENV.showTelegramCard)}</SelectItem>
                        <SelectItem value="on">Mostrar</SelectItem>
                        <SelectItem value="off">Ocultar</SelectItem>
                      </SelectContent>
                    </Select>
                    {simpleShowTelegramCard === "inherit" && (
                      <p className="text-[10.5px] leading-tight text-zinc-500">
                        Sin override admin · template usa <code className="text-[10px]">{INHERITED_FROM_ENV.showTelegramCard ? "true" : "false"}</code>
                      </p>
                    )}
                  </div>
                  <div className="space-y-1.5">
                    <Label>Card Mi VIP</Label>
                    <Select value={simpleShowVipCard} onValueChange={(v: MuxTriState) => setSimpleShowVipCard(v)}>
                      <SelectTrigger className="border-white/[0.08] bg-zinc-700/50"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="inherit">{inheritLabel(INHERITED_FROM_ENV.showVipCard)}</SelectItem>
                        <SelectItem value="on">Mostrar</SelectItem>
                        <SelectItem value="off">Ocultar</SelectItem>
                      </SelectContent>
                    </Select>
                    {simpleShowVipCard === "inherit" && (
                      <p className="text-[10.5px] leading-tight text-zinc-500">
                        Sin override admin · template usa <code className="text-[10px]">{INHERITED_FROM_ENV.showVipCard ? "true" : "false"}</code>
                      </p>
                    )}
              </div>
                  <div className="space-y-1.5">
                    <Label>Botón “Canal gratis” (video landing)</Label>
                    <Select value={dualCtaFreeTelegram} onValueChange={(v: MuxTriState) => setDualCtaFreeTelegram(v)}>
                      <SelectTrigger className="border-white/[0.08] bg-zinc-700/50"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="inherit">Auto (mostrar si hay URL)</SelectItem>
                        <SelectItem value="on">Mostrar</SelectItem>
                        <SelectItem value="off">Ocultar</SelectItem>
                      </SelectContent>
                    </Select>
                    <p className="text-[10.5px] leading-tight text-zinc-500">
                      {dualCtaFreeTelegram === "off"
                        ? "Oculto en DualCTAVideoLanding (/video-landing)."
                        : "Botón Telegram gratis bajo el CTA de membresía."}
                    </p>
                  </div>
            </div>

                <div className="grid gap-3 sm:grid-cols-2">
                  <div className="flex items-center justify-between gap-3 rounded-xl border border-white/[0.07] bg-zinc-700/30 px-3 py-2.5">
                    <div className="min-w-0 space-y-0.5">
                      <div className="flex items-center gap-1.5">
                        <Label htmlFor="lock-tg" className="text-sm">Bloquear Telegram</Label>
                        <HelpHint label="Diferencia entre bloquear y ocultar">
                          <strong>Bloquear</strong>: card visible pero atenuada, taps no abren checkout — van al embudo + PostHog (mide intención sin vender).{" "}
                          <br />
                          <strong>Ocultar</strong>: la card no se renderiza.
                        </HelpHint>
                      </div>
                      <p className="text-[11px] text-muted-foreground">Sin checkout · taps → embudo + PostHog</p>
                    </div>
                    <Switch
                      id="lock-tg"
                      checked={simpleTelegramCardLocked}
                      disabled={simpleShowTelegramCard === "off"}
                      onCheckedChange={setSimpleTelegramCardLocked}
                    />
                  </div>
                  <div className="flex items-center justify-between gap-3 rounded-xl border border-white/[0.07] bg-zinc-700/30 px-3 py-2.5">
                    <div className="min-w-0 space-y-0.5">
                      <div className="flex items-center gap-1.5">
                        <Label htmlFor="lock-vip" className="text-sm">Bloquear Mi VIP</Label>
                        <HelpHint label="Diferencia entre bloquear y ocultar">
                          <strong>Bloquear</strong>: card visible pero atenuada, taps no abren checkout — van al embudo + PostHog (mide intención sin vender).{" "}
                          <br />
                          <strong>Ocultar</strong>: la card no se renderiza.
                        </HelpHint>
                      </div>
                      <p className="text-[11px] text-muted-foreground">Sin checkout · taps → embudo + PostHog</p>
                    </div>
                    <Switch
                      id="lock-vip"
                      checked={simpleVipCardLocked}
                      disabled={simpleShowVipCard === "off"}
                      onCheckedChange={setSimpleVipCardLocked}
                    />
                  </div>
                </div>
              </div>

              <div className="rounded-2xl border border-white/[0.07] bg-zinc-800/30 p-5 space-y-4">
                <div>
                  <h2 className="text-sm font-semibold text-foreground">Texto de las cards</h2>
                  <p className="mt-0.5 text-xs text-muted-foreground">
                    Título y CTA de cada card clickeable en la parte inferior de la página.
                    Vacío = traducción automática por idioma del usuario.
                  </p>
                </div>

                <div className="space-y-3">
                  <p className="text-[11px] font-medium uppercase tracking-wide text-muted-foreground/70">Card Telegram</p>
                  <div className="grid gap-3 sm:grid-cols-2">
                    <div className="space-y-1.5">
                      <Label htmlFor="tg-card-title" className="text-xs">Título</Label>
                      <Input
                        id="tg-card-title"
                        value={getLT("telegramCardTitle")}
                        onChange={(e) => setLT("telegramCardTitle", e.target.value)}
                        placeholder="Ej: Grupo privado"
                        maxLength={120}
                        disabled={simpleShowTelegramCard === "off"}
                        className="border-white/[0.08] bg-zinc-700/50 text-sm"
                      />
                    </div>
                    <div className="space-y-1.5">
                      <Label htmlFor="tg-card-cta" className="text-xs">CTA (botón)</Label>
                      <Input
                        id="tg-card-cta"
                        value={getLT("telegramCta")}
                        onChange={(e) => setLT("telegramCta", e.target.value)}
                        placeholder="Ej: Entrar"
                        maxLength={80}
                        disabled={simpleShowTelegramCard === "off"}
                        className="border-white/[0.08] bg-zinc-700/50 text-sm"
                      />
                    </div>
                  </div>
                </div>

                <div className="space-y-3">
                  <p className="text-[11px] font-medium uppercase tracking-wide text-muted-foreground/70">Card Mi VIP</p>
                  <div className="grid gap-3 sm:grid-cols-2">
                    <div className="space-y-1.5">
                      <Label htmlFor="vip-card-title" className="text-xs">Título</Label>
                      <Input
                        id="vip-card-title"
                        value={getLT("membershipCardTitle")}
                        onChange={(e) => setLT("membershipCardTitle", e.target.value)}
                        placeholder="Ej: Mi VIP"
                        maxLength={120}
                        disabled={simpleShowVipCard === "off"}
                        className="border-white/[0.08] bg-zinc-700/50 text-sm"
                      />
                    </div>
                    <div className="space-y-1.5">
                      <Label htmlFor="vip-card-cta" className="text-xs">CTA (botón)</Label>
                      <Input
                        id="vip-card-cta"
                        value={getLT("membershipCta")}
                        onChange={(e) => setLT("membershipCta", e.target.value)}
                        placeholder="Ej: Ir al VIP"
                        maxLength={80}
                        disabled={simpleShowVipCard === "off"}
                        className="border-white/[0.08] bg-zinc-700/50 text-sm"
                      />
                    </div>
                  </div>
                  <div className="space-y-1.5">
                    <Label htmlFor="vip-card-subtitle" className="text-xs">Subtítulo</Label>
                    <Input
                      id="vip-card-subtitle"
                      value={getLT("membershipCardSubtitle")}
                      onChange={(e) => setLT("membershipCardSubtitle", e.target.value)}
                      placeholder="Ej: Chat privado y contenido exclusivo"
                      maxLength={200}
                      disabled={simpleShowVipCard === "off"}
                      className="border-white/[0.08] bg-zinc-700/50 text-sm"
                    />
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* ── Section: Overlay video (Mux) ──────────────────────────────── */}
        <div id="overlay-video-bio-premium-simple" className={cn(
          "overflow-hidden rounded-2xl border bg-zinc-900/50",
          simpleMuxOverlayEnabled ? "border-amber-500/40" : "border-white/[0.08]",
        )}>
          <button
            type="button"
            onClick={() => toggleSection("overlay-video")}
            className="flex w-full items-center gap-3 px-5 py-4 text-left transition-colors hover:bg-white/[0.03]"
          >
            <Tv2 className="h-4 w-4 shrink-0 text-amber-400" aria-hidden />
            <div className="flex flex-1 min-w-0 flex-col gap-1">
              <div className="flex flex-wrap items-center gap-1.5">
                <span className="text-sm font-semibold text-foreground">Overlay video (Mux)</span>
                <SurfaceBadge surface="overlay" />
                {simpleMuxOverlayEnabled && (
                  <span className="inline-flex items-center rounded-full border border-green-700/50 bg-green-950/60 px-2 py-0.5 text-[10px] font-medium text-green-400">
                    Activo
                  </span>
                )}
              </div>
              <span className="text-[11px] text-muted-foreground">Video fullscreen que cubre toda la pantalla al cargar · con botones VIP + Telegram gratis</span>
            </div>
            <ChevronDown className={cn("h-4 w-4 shrink-0 text-zinc-500 transition-transform duration-200", openSections.has("overlay-video") && "rotate-180")} aria-hidden />
          </button>
          {openSections.has("overlay-video") && (() => {
            const effectiveFreeTelegramForOverlay = freeTelegramUrl.trim() || EFFECTIVE_FREE_TELEGRAM_URL;
            const hasOverlayVideo = Boolean(TEMPLATE_PREREQS.hasVipMux || simpleMuxOverlayPlaybackId.trim());
            const vipButtonOk = hasOverlayVideo;
            const telegramButtonOk = Boolean(effectiveFreeTelegramForOverlay);
            return (
            <div className="border-t border-white/[0.06] space-y-4 px-5 pb-5 pt-4">
              <p className="text-xs text-muted-foreground">
                Muestra un video <strong className="text-muted-foreground">fullscreen sobre toda la página</strong> en cuanto el usuario abre la landing.
                El video trae botones VIP (dorado) y Telegram gratis. El usuario lo cierra para ver las cards.
                Aplica a <code className="text-[10px]">/invitacion-premium</code> y a la home del creator (solo si no está en modo telegram-only).
              </p>

              <div className="flex items-center justify-between gap-3 rounded-2xl border border-white/[0.07] bg-zinc-800/30 px-4 py-3.5">
                <div className="min-w-0 space-y-0.5">
                  <Label htmlFor="mux-overlay-on" className="text-sm font-semibold">Activar overlay</Label>
                  <p className="text-[11px] text-muted-foreground">Enciende el video fullscreen · el usuario lo ve <em>antes</em> de ver las cards</p>
                </div>
                <Switch
                  id="mux-overlay-on"
                  checked={simpleMuxOverlayEnabled}
                  onCheckedChange={setSimpleMuxOverlayEnabled}
                />
              </div>

              <div className={cn("space-y-1.5 transition-opacity", !simpleMuxOverlayEnabled && "pointer-events-none opacity-40")}>
                <Label htmlFor="mux-overlay-pid" className="text-xs">Video del overlay</Label>
                <MuxVideoPicker
                  value={simpleMuxOverlayPlaybackId}
                  onChange={setSimpleMuxOverlayPlaybackId}
                  placeholder="Vacío = usa el mismo video VIP de la card Mi VIP"
                  disabled={!simpleMuxOverlayEnabled}
                />
                <p className="text-[10px] text-muted-foreground">
                  ID Mux del video a reproducir en el overlay. Si está vacío, reutiliza el video de membresía ya configurado en Railway.
                </p>
              </div>

              {/* ── Botones dentro del overlay ──────────────────────────── */}
              <div className={cn("space-y-3 transition-opacity", !simpleMuxOverlayEnabled && "pointer-events-none opacity-40")}>
                <div className="flex items-center justify-between gap-2">
                  <p className="text-[11px] font-medium uppercase tracking-wide text-muted-foreground/70">Botones dentro del video</p>
                  <span className="text-[10px] text-muted-foreground/60">Idioma: <strong className="text-zinc-300">{LANG_LABELS[activeLang]}</strong></span>
                </div>

                {/* Botón VIP (dorado) */}
                <div className="rounded-2xl border border-amber-500/20 bg-amber-950/10 p-4 space-y-2.5">
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="inline-flex items-center rounded-md bg-amber-500 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide text-black">VIP</span>
                    <span className="text-xs font-semibold text-amber-200">Botón dorado · va al checkout VIP</span>
                    {vipButtonOk ? (
                      <span className="inline-flex items-center rounded-full border border-emerald-700/40 bg-emerald-950/50 px-2 py-0.5 text-[10px] font-medium text-emerald-300">Listo</span>
                    ) : (
                      <span className="inline-flex items-center rounded-full border border-red-700/40 bg-red-950/40 px-2 py-0.5 text-[10px] font-medium text-red-300">Sin video Mux</span>
                    )}
                  </div>
                  <div className="space-y-1.5">
                    <Label htmlFor="overlay-vip-cta" className="text-xs">Texto del botón VIP</Label>
                    <Input
                      id="overlay-vip-cta"
                      value={getLT("membershipCta")}
                      onChange={(e) => setLT("membershipCta", e.target.value)}
                      placeholder='Ej: "Mi VIP" o "Ir al VIP"'
                      maxLength={80}
                      disabled={!simpleMuxOverlayEnabled}
                      className="border-white/[0.08] bg-zinc-700/50 text-sm"
                    />
                    <p className="text-[10px] text-muted-foreground">
                      Mismo texto que la card Mi VIP. Acción: abre el checkout VIP (Stripe internacional · MercadoPago AR).
                    </p>
                  </div>
                </div>

                {/* Botón Telegram gratis */}
                <div className={cn(
                  "rounded-2xl border bg-zinc-800/30 p-4 space-y-2.5",
                  telegramButtonOk ? "border-emerald-700/25" : "border-amber-700/40",
                )}>
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="inline-flex items-center rounded-md bg-emerald-600 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide text-white">FREE</span>
                    <span className="text-xs font-semibold text-emerald-200">Botón secundario · abre Telegram gratis</span>
                    {telegramButtonOk ? (
                      <span className="inline-flex items-center rounded-full border border-emerald-700/40 bg-emerald-950/50 px-2 py-0.5 text-[10px] font-medium text-emerald-300">Listo</span>
                    ) : (
                      <span className="inline-flex items-center rounded-full border border-amber-700/40 bg-amber-950/40 px-2 py-0.5 text-[10px] font-medium text-amber-300">No aparecerá · falta URL</span>
                    )}
                  </div>
                  <div className="space-y-1.5">
                    <Label htmlFor="overlay-tg-cta" className="text-xs">Texto del botón Telegram</Label>
                    <Input
                      id="overlay-tg-cta"
                      value={getLT("freeTelegramCta")}
                      onChange={(e) => setLT("freeTelegramCta", e.target.value)}
                      placeholder='Ej: "Telegram gratis" o "Canal público"'
                      maxLength={80}
                      disabled={!simpleMuxOverlayEnabled}
                      className="border-white/[0.08] bg-zinc-700/50 text-sm"
                    />
                  </div>
                  <div className="rounded-lg border border-white/[0.06] bg-zinc-900/60 px-3 py-2 space-y-1">
                    <p className="text-[10px] uppercase tracking-wide text-muted-foreground/70">URL destino actual</p>
                    {effectiveFreeTelegramForOverlay ? (
                      <p className="break-all font-mono text-[11px] text-emerald-300">{effectiveFreeTelegramForOverlay}</p>
                    ) : (
                      <p className="text-[11px] text-amber-300">— Sin URL configurada: el botón no se mostrará en el overlay.</p>
                    )}
                    <p className="text-[10px] text-muted-foreground/70">
                      Editar URL en sección <button type="button" onClick={() => { if (!openSections.has("embudo")) toggleSection("embudo"); const el = document.getElementById("tg-url"); el?.scrollIntoView({ behavior: "smooth", block: "center" }); el?.focus?.(); }} className="underline underline-offset-2 hover:text-zinc-300">URLs, funnel y pantalla final</button>.
                    </p>
                  </div>
                </div>

                <p className="text-[10px] leading-snug text-muted-foreground/70">
                  Los textos de ambos botones se gestionan por idioma (cambialo arriba en la barra de idiomas). Los textos son los mismos que usan las cards equivalentes — editar acá impacta también en las cards.
                </p>
              </div>
            </div>
            );
          })()}
        </div>

        {/* ── Section: Textos adicionales ───────────────────────────────── */}
        <div id="extra-texts-bio-premium-simple" className="overflow-hidden rounded-2xl border border-white/[0.08] bg-zinc-900/50">
          <button
            type="button"
            onClick={() => toggleSection("extra-texts")}
            className="flex w-full items-center gap-3 px-5 py-4 text-left transition-colors hover:bg-white/[0.03]"
          >
            <Type className="h-4 w-4 shrink-0 text-amber-400" aria-hidden />
            <div className="flex flex-1 min-w-0 flex-col gap-1">
              <div className="flex flex-wrap items-center gap-1.5">
                <span className="text-sm font-semibold text-foreground">Micro-textos y hints</span>
                <SurfaceBadge surface="simple" />
                <SurfaceBadge surface="home" />
              </div>
              <span className="text-[11px] text-muted-foreground">Chip "Recomendado" sobre la card VIP, texto del botón en el video de membresía, hints y pie de página</span>
            </div>
            <ChevronDown className={cn("h-4 w-4 shrink-0 text-zinc-500 transition-transform duration-200", openSections.has("extra-texts") && "rotate-180")} aria-hidden />
          </button>
          {openSections.has("extra-texts") && (
            <div className="border-t border-white/[0.06] space-y-4 px-5 pb-5 pt-4">
              <p className="text-xs text-muted-foreground">
                Elementos secundarios de la página: chip "Recomendado" encima de la card VIP, texto del botón en el video de membresía, hints contextuales y línea del footer.
                Vacío = usar texto por idioma del código.
              </p>
              <div className="rounded-2xl border border-white/[0.07] bg-zinc-800/30 p-5 space-y-4">
                <div className="space-y-1.5">
                  <label className="text-xs font-medium text-zinc-300">Chip "Recomendado" (encima de la card VIP)</label>
                  <input type="text" value={getLT("vipRecommendedBadge")} onChange={(e) => setLT("vipRecommendedBadge", e.target.value)} maxLength={40} placeholder='Ej: "Recomendado"' className="w-full rounded-xl border border-white/10 bg-zinc-900 px-3 py-2 text-sm text-white placeholder:text-zinc-600 focus:outline-none focus:ring-2 focus:ring-amber-500/40" />
                </div>
                <div className="space-y-1.5">
                  <label className="text-xs font-medium text-zinc-300">Texto del botón en el video VIP</label>
                  <input type="text" value={getLT("membershipVideoCta")} onChange={(e) => setLT("membershipVideoCta", e.target.value)} maxLength={60} placeholder='Ej: "Miembros Exclusivos"' className="w-full rounded-xl border border-white/10 bg-zinc-900 px-3 py-2 text-sm text-white placeholder:text-zinc-600 focus:outline-none focus:ring-2 focus:ring-amber-500/40" />
                </div>
                <div className="space-y-1.5">
                  <label className="text-xs font-medium text-zinc-300">Hint "tocá el video" (solo si hay video)</label>
                  <input type="text" value={getLT("vipTapHint")} onChange={(e) => setLT("vipTapHint", e.target.value)} maxLength={60} placeholder='Ej: "Tocá el video para continuar"' className="w-full rounded-xl border border-white/10 bg-zinc-900 px-3 py-2 text-sm text-white placeholder:text-zinc-600 focus:outline-none focus:ring-2 focus:ring-amber-500/40" />
                </div>
                <div className="space-y-1.5">
                  <label className="text-xs font-medium text-zinc-300">Hint debajo del botón Telegram (activación)</label>
                  <input type="text" value={getLT("telegramActivationHint")} onChange={(e) => setLT("telegramActivationHint", e.target.value)} maxLength={120} placeholder='Ej: "Activación manual en 24 hs"' className="w-full rounded-xl border border-white/10 bg-zinc-900 px-3 py-2 text-sm text-white placeholder:text-zinc-600 focus:outline-none focus:ring-2 focus:ring-amber-500/40" />
                </div>
                <div className="space-y-1.5">
                  <label className="text-xs font-medium text-zinc-300">Línea del footer</label>
                  <input type="text" value={getLT("footerTrustLine")} onChange={(e) => setLT("footerTrustLine", e.target.value)} maxLength={120} placeholder='Ej: "Pago seguro · Cancelá cuando quieras"' className="w-full rounded-xl border border-white/10 bg-zinc-900 px-3 py-2 text-sm text-white placeholder:text-zinc-600 focus:outline-none focus:ring-2 focus:ring-amber-500/40" />
                </div>
              </div>
            </div>
          )}
        </div>

        {/* ── Section: Layout & Reels ────────────────────────────────────── */}
        <div id="layout-reels" className="overflow-hidden rounded-2xl border border-white/[0.08] bg-zinc-900/50">
          <button
            type="button"
            onClick={() => toggleSection("layout-reels")}
            className="flex w-full items-center gap-3 px-5 py-4 text-left transition-colors hover:bg-white/[0.03]"
          >
            <Tv2 className="h-4 w-4 shrink-0 text-indigo-400" aria-hidden />
            <div className="flex flex-1 min-w-0 flex-col gap-1">
              <div className="flex flex-wrap items-center gap-1.5">
                <span className="text-sm font-semibold text-foreground">Layout & Reels — A/B y player</span>
                <SurfaceBadge surface="simple" />
                <SurfaceBadge surface="reels" />
              </div>
              <span className="text-[11px] text-muted-foreground">Qué template ve el usuario al entrar a /invitacion-premium · porcentaje de tráfico a Reels · clips y variante del player</span>
            </div>
            <ChevronDown className={cn("h-4 w-4 shrink-0 text-zinc-500 transition-transform duration-200", openSections.has("layout-reels") && "rotate-180")} aria-hidden />
          </button>
          {openSections.has("layout-reels") && (
            <div className="border-t border-white/[0.06] space-y-4 px-5 pb-5 pt-4">

              <div className="rounded-2xl border border-white/[0.07] bg-zinc-800/30 p-5 space-y-5">
                <div>
                  <h2 className="text-sm font-semibold text-foreground">A/B: landing clásica vs feed Reels</h2>
                  <p className="mt-0.5 text-xs text-muted-foreground">
                    Controla qué experiencia ve el usuario al entrar a <code className="text-[10px]">/invitacion-premium</code>.
                  </p>
                </div>

                <div className="flex items-start gap-3 rounded-xl border border-amber-500/25 bg-amber-950/30 p-3 text-xs text-amber-100/90">
                  <Info className="mt-0.5 h-3.5 w-3.5 shrink-0" aria-hidden />
                  <span>
                    <strong>Forzar layout</strong> tiene prioridad sobre PostHog y %. No anula{" "}
                    <code>?invite_layout=</code> ni env variables de entorno. Con <strong>Experimento apagado</strong>, todos ven la landing clásica.
                  </span>
                </div>

                <div className="flex items-center justify-between gap-4">
                  <div>
                    <Label htmlFor="exp-on">Experimento activo</Label>
                    <p className="text-xs text-muted-foreground">Si está off, siempre landing clásica.</p>
                  </div>
                  <Switch id="exp-on" checked={experimentEnabled} onCheckedChange={setExperimentEnabled} />
                </div>

                <div className="flex items-center justify-between gap-4">
                  <div>
                    <Label htmlFor="ph-on">Usar PostHog para elegir layout</Label>
                    <p className="text-xs text-muted-foreground">
                      Flag <code className="text-[10px]">premium_invite_layout</code>. Si off, solo % + env.
                    </p>
                  </div>
                  <Switch id="ph-on" checked={usePosthogLayout} onCheckedChange={setUsePosthogLayout} />
                </div>

                <div className="grid gap-4 sm:grid-cols-2">
                  <div className="space-y-2">
                    <div className="flex items-center gap-1.5">
                      <Label>Forzar layout</Label>
                      <HelpHint label="Prioridad de Forzar layout">
                        Tiene prioridad sobre PostHog y % de rollout. Solo se anula con <code>?invite_layout=</code> en la URL o env variables de entorno. Usalo para emergencias o pruebas internas.
                      </HelpHint>
                    </div>
                    <Select value={forceLayout} onValueChange={(v: ForceLayoutOpt) => setForceLayout(v)}>
                      <SelectTrigger className="border-white/[0.08] bg-zinc-700/50">
                        <SelectValue placeholder="Automático" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="auto">Automático (PostHog y %)</SelectItem>
                        <SelectItem value="premium_simple">Siempre landing clásica</SelectItem>
                        <SelectItem value="reels_feed">Siempre Reels</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <div className="flex items-center gap-1.5">
                      <Label htmlFor="rollout">% tráfico a Reels</Label>
                      <HelpHint label="Rollout porcentual">
                        Solo aplica si <strong>Forzar layout = Automático</strong> y PostHog no resolvió el flag. Asigna pseudo-aleatoriamente N% de visitantes a Reels (persistente por sesión).
                      </HelpHint>
                    </div>
                    <Input
                      id="rollout"
                      type="number"
                      min={0}
                      max={100}
                      value={reelsRollout}
                      onChange={(e) => {
                        const v = e.target.value;
                        setReelsRollout(v === "" ? 0 : clampRollout(Number(v), reelsRollout));
                      }}
                      className="border-white/[0.08] bg-zinc-700/50"
                    />
                    <p className="text-xs text-muted-foreground">
                      Solo si PostHog no define layout. Fallback:{" "}
                      <code className="text-[10px]">VITE_PREMIUM_INVITE_REELS_PERCENT</code>.
                    </p>
                  </div>
                </div>
              </div>

              <div className="rounded-2xl border border-white/[0.07] bg-zinc-800/30 p-5 space-y-5">
                <div>
                  <h2 className="text-sm font-semibold text-foreground">Reels: clips y player</h2>
                  <p className="mt-0.5 text-xs text-muted-foreground">
                    Origen de los videos y comportamiento del player en el feed Reels.
                  </p>
                </div>

                <div className="space-y-2">
                  <Label>Origen del listado de videos</Label>
                  <Select value={reelsPlaybackSource} onValueChange={(v: ReelsPlaybackSource) => setReelsPlaybackSource(v)}>
                    <SelectTrigger className="border-white/[0.08] bg-zinc-700/50"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="mux_api">
                        Automático — API Mux (
                        <code className="text-[10px]">
                          creator_id=brand · external_id=feed
                        </code>
                        )
                      </SelectItem>
                      <SelectItem value="curated">Lista curada — IDs fijos, sin llamada al listado</SelectItem>
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-muted-foreground">
                    Con lista curada, <strong className="text-muted-foreground">/invitacion-premium</strong> y{" "}
                    <strong className="text-muted-foreground">/reels</strong> usan los mismos IDs.
                  </p>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="curated-ids">Playback IDs Mux (uno por línea o coma)</Label>
                  <Textarea
                    id="curated-ids"
                    value={curatedPlaybackText}
                    onChange={(e) => setCuratedPlaybackText(e.target.value)}
                    placeholder="Ej: abcDEFGhij0123456789..."
                    disabled={reelsPlaybackSource !== "curated"}
                    rows={6}
                    className="border-white/[0.08] bg-zinc-700/50 font-mono text-sm"
                  />
                  <p className="text-xs text-muted-foreground">
                    Máx. {PREMIUM_INVITE_MAX_CURATED_REELS_IDS} IDs. Solo alfanuméricos, guiones y guiones bajos.
                  </p>
                  {reelsPlaybackSource === "curated" && (
                    <p className="text-xs text-muted-foreground">
                      {curatedSummary.validIds.length} válidos · tope {PREMIUM_INVITE_MAX_CURATED_REELS_IDS}
                      {curatedSummary.invalidTokenCount > 0
                        ? ` · ${curatedSummary.invalidTokenCount} con formato inválido`
                        : ""}
                      {curatedSummary.duplicateTokenCount > 0
                        ? ` · ${curatedSummary.duplicateTokenCount} duplicados omitidos`
                        : ""}
                    </p>
                  )}
                </div>

                <div className="grid gap-4 sm:grid-cols-2">
                  <div className="space-y-2">
                    <Label htmlFor="teaser">Reels en modo teaser (N)</Label>
                    <Input
                      id="teaser"
                      type="number"
                      min={1}
                      max={20}
                      value={teaserCount}
                      onChange={(e) => {
                        const v = e.target.value;
                        setTeaserCount(v === "" ? 1 : clampTeaserCount(Number(v), teaserCount));
                      }}
                      className="border-white/[0.08] bg-zinc-700/50"
                    />
                    <p className="text-xs text-muted-foreground">Solo afecta variante <code className="text-[10px]">three_teaser</code>.</p>
                  </div>
                  <div className="space-y-2">
                    <Label>Variante del player</Label>
                    <Select value={playerVariant} onValueChange={(v: ReelsPlayerVariant) => setPlayerVariant(v)}>
                      <SelectTrigger className="border-white/[0.08] bg-zinc-700/50"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="use_posthog">PostHog: flag <code className="text-[10px]">reels_feed_ab_style</code></SelectItem>
                        <SelectItem value="three_teaser">Teaser: primeros N + CTA "ver más"</SelectItem>
                        <SelectItem value="classic">Lista completa + pantalla final</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* ── Section: Embudo & Texto ────────────────────────────────────── */}
        <div id="embudo" className="overflow-hidden rounded-2xl border border-white/[0.08] bg-zinc-900/50">
          <button
            type="button"
            onClick={() => toggleSection("embudo")}
            className="flex w-full items-center gap-3 px-5 py-4 text-left transition-colors hover:bg-white/[0.03]"
          >
            <Layers className="h-4 w-4 shrink-0 text-emerald-400" aria-hidden />
            <div className="flex flex-1 min-w-0 flex-col gap-1">
              <div className="flex flex-wrap items-center gap-1.5">
                <span className="text-sm font-semibold text-foreground">URLs, funnel y pantalla final</span>
                <SurfaceBadge surface="simple" />
                <SurfaceBadge surface="home" />
                <SurfaceBadge surface="reels" />
              </div>
              <span className="text-[11px] text-muted-foreground">URL del canal gratis y base del checkout VIP · variante friendly de la card Mi VIP · pantalla final del feed Reels</span>
            </div>
            <ChevronDown className={cn("h-4 w-4 shrink-0 text-zinc-500 transition-transform duration-200", openSections.has("embudo") && "rotate-180")} aria-hidden />
          </button>
          {openSections.has("embudo") && (
            <div className="border-t border-white/[0.06] space-y-4 px-5 pb-5 pt-4">

              {/* ── Precios VIP ──────────────────────────────────────────────── */}
              <div className="rounded-2xl border border-amber-500/20 bg-amber-950/20 p-5 space-y-4">
                <div>
                  <div className="flex flex-wrap items-center gap-2">
                    <h2 className="text-sm font-semibold text-foreground">Precios VIP</h2>
                    <SurfaceBadge surface="overlay" />
                  </div>
                  <p className="mt-1 text-xs text-muted-foreground">
                    Precio de la membresía VIP cobrado en el checkout. Cambia en tiempo real — no requiere deploy.
                    En la landing de video (<code>/video-landing</code>) se cobra en <strong>USD</strong> y el
                    valor de <strong>Precio VIP USD</strong> es la fuente de verdad: tiene prioridad sobre el
                    Stripe Price ID de <code>/admin/suscripciones</code>. Vacío = heredar{" "}
                    <code>VITE_PREMIUM_USD_VIP_PRICE</code> / <code>VITE_PREMIUM_ARS_VIP_PRICE</code>.
                  </p>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="vip-price-usd">Precio VIP USD</Label>
                    <Input
                      id="vip-price-usd"
                      type="number"
                      min="0"
                      step="0.01"
                      value={vipPriceUsd}
                      onChange={(e) => setVipPriceUsd(e.target.value)}
                      placeholder="49.99"
                      className="border-white/[0.08] bg-zinc-700/50 font-mono text-sm"
                    />
                    {!vipPriceUsd.trim() && (
                      <p className="text-[10.5px] leading-tight text-zinc-500">
                        Vacío · usa env/default <code className="text-[10px]">$49.99</code>
                      </p>
                    )}
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="vip-price-ars">Precio VIP ARS</Label>
                    <Input
                      id="vip-price-ars"
                      type="number"
                      min="0"
                      step="1"
                      value={vipPriceArs}
                      onChange={(e) => setVipPriceArs(e.target.value)}
                      placeholder="49999"
                      className="border-white/[0.08] bg-zinc-700/50 font-mono text-sm"
                    />
                    {!vipPriceArs.trim() && (
                      <p className="text-[10.5px] leading-tight text-zinc-500">
                        Vacío · usa env/default <code className="text-[10px]">$49999</code>
                      </p>
                    )}
                  </div>
                </div>
              </div>

              {/* ── Precios — funnel /invitacion-premium ─────────────────────── */}
              <div className="rounded-2xl border border-sky-500/20 bg-sky-950/20 p-5 space-y-4">
                <div>
                  <div className="flex flex-wrap items-center gap-2">
                    <h2 className="text-sm font-semibold text-foreground">Precios — funnel /invitacion-premium</h2>
                    <SurfaceBadge surface="simple" />
                  </div>
                  <p className="mt-1 text-xs text-muted-foreground">
                    Plan Standard (USD/ARS) y plan Telegram Basic del layout simple. Vacío = usa env/default.
                  </p>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="standard-price-usd">Precio Standard USD</Label>
                    <Input
                      id="standard-price-usd"
                      type="number"
                      min="0"
                      step="0.01"
                      value={standardPriceUsd}
                      onChange={(e) => setStandardPriceUsd(e.target.value)}
                      placeholder="29.99"
                      className="border-white/[0.08] bg-zinc-700/50 font-mono text-sm"
                    />
                    {!standardPriceUsd.trim() && (
                      <p className="text-[10.5px] leading-tight text-zinc-500">
                        Vacío = usa env/default <code className="text-[10px]">$29.99</code>
                      </p>
                    )}
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="standard-price-ars">Precio Standard ARS</Label>
                    <Input
                      id="standard-price-ars"
                      type="number"
                      min="0"
                      step="1"
                      value={standardPriceArs}
                      onChange={(e) => setStandardPriceArs(e.target.value)}
                      placeholder="29999"
                      className="border-white/[0.08] bg-zinc-700/50 font-mono text-sm"
                    />
                    {!standardPriceArs.trim() && (
                      <p className="text-[10.5px] leading-tight text-zinc-500">
                        Vacío = usa env/default <code className="text-[10px]">$29999</code>
                      </p>
                    )}
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="telegram-basic-cents">Telegram Basic (centavos USD)</Label>
                    <Input
                      id="telegram-basic-cents"
                      type="number"
                      min="0"
                      step="1"
                      value={telegramBasicPriceCents}
                      onChange={(e) => setTelegramBasicPriceCents(e.target.value)}
                      placeholder="699"
                      className="border-white/[0.08] bg-zinc-700/50 font-mono text-sm"
                    />
                    {!telegramBasicPriceCents.trim() && (
                      <p className="text-[10.5px] leading-tight text-zinc-500">
                        Centavos USD (699 = $6.99) · Vacío = usa env/default <code className="text-[10px]">699</code>
                      </p>
                    )}
                  </div>
                </div>
              </div>

              {/* ── Precios — otros surfaces ─────────────────────────────────── */}
              <div className="rounded-2xl border border-emerald-500/20 bg-emerald-950/20 p-5 space-y-4">
                <div>
                  <h2 className="text-sm font-semibold text-foreground">Precios — otros surfaces</h2>
                  <p className="mt-1 text-xs text-muted-foreground">
                    /ar/invitacion (ARS), donación VIP internacional (USD) y regalo Sorprendeme. Vacío = usa env/default.
                  </p>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="ar-invitacion-standard-ars">/ar/invitacion · Standard ARS</Label>
                    <Input
                      id="ar-invitacion-standard-ars"
                      type="number"
                      min="0"
                      step="1"
                      value={arInvitacionStandardPriceArs}
                      onChange={(e) => setArInvitacionStandardPriceArs(e.target.value)}
                      placeholder="29999"
                      className="border-white/[0.08] bg-zinc-700/50 font-mono text-sm"
                    />
                    {!arInvitacionStandardPriceArs.trim() && (
                      <p className="text-[10.5px] leading-tight text-zinc-500">
                        Vacío = usa env/default <code className="text-[10px]">$29999</code>
                      </p>
                    )}
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="ar-invitacion-vip-ars">/ar/invitacion · VIP ARS</Label>
                    <Input
                      id="ar-invitacion-vip-ars"
                      type="number"
                      min="0"
                      step="1"
                      value={arInvitacionVipPriceArs}
                      onChange={(e) => setArInvitacionVipPriceArs(e.target.value)}
                      placeholder="49999"
                      className="border-white/[0.08] bg-zinc-700/50 font-mono text-sm"
                    />
                    {!arInvitacionVipPriceArs.trim() && (
                      <p className="text-[10.5px] leading-tight text-zinc-500">
                        Vacío = usa env/default <code className="text-[10px]">$49999</code>
                      </p>
                    )}
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="vip-donation-usd">Donación VIP internacional USD</Label>
                    <Input
                      id="vip-donation-usd"
                      type="number"
                      min="0"
                      step="1"
                      value={vipDonationSuggestedUsd}
                      onChange={(e) => setVipDonationSuggestedUsd(e.target.value)}
                      placeholder="300"
                      className="border-white/[0.08] bg-zinc-700/50 font-mono text-sm"
                    />
                    {!vipDonationSuggestedUsd.trim() && (
                      <p className="text-[10.5px] leading-tight text-zinc-500">
                        Monto sugerido · Vacío = usa default <code className="text-[10px]">$300</code>
                      </p>
                    )}
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="sorprendeme-min">Sorprendeme · mínimo USD</Label>
                    <Input
                      id="sorprendeme-min"
                      type="number"
                      min="0"
                      step="1"
                      value={sorprendemeMinAmount}
                      onChange={(e) => setSorprendemeMinAmount(e.target.value)}
                      placeholder="50"
                      className="border-white/[0.08] bg-zinc-700/50 font-mono text-sm"
                    />
                    {!sorprendemeMinAmount.trim() && (
                      <p className="text-[10.5px] leading-tight text-zinc-500">
                        Vacío = usa default <code className="text-[10px]">$50</code>
                      </p>
                    )}
                  </div>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="sorprendeme-tiers">Sorprendeme · chips de monto (JSON)</Label>
                  <Textarea
                    id="sorprendeme-tiers"
                    value={sorprendemeAmountTiers}
                    onChange={(e) => setSorprendemeAmountTiers(e.target.value)}
                    placeholder={'[{"amount":50,"label":"Classic","emoji":"❤️‍🔥"},{"amount":99,"label":"Premium","emoji":"🔥"}]'}
                    rows={4}
                    className="border-white/[0.08] bg-zinc-700/50 font-mono text-xs"
                  />
                  {!sorprendemeAmountTiers.trim() && (
                    <p className="text-[10.5px] leading-tight text-zinc-500">
                      JSON <code className="text-[10px]">[{'{'}amount,label,emoji?{'}'}]</code> · Vacío = usa los chips por defecto en código
                    </p>
                  )}
                </div>
              </div>

              <div className="rounded-2xl border border-white/[0.07] bg-zinc-800/30 p-5 space-y-4">
                <div>
                  <div className="flex flex-wrap items-center gap-2">
                    <h2 className="text-sm font-semibold text-foreground">URLs del embudo</h2>
                    <SurfaceBadge surface="simple" />
                    <SurfaceBadge surface="home" />
                    <SurfaceBadge surface="reels" />
                    <SurfaceBadge surface="overlay" />
                  </div>
                  <p className="mt-1 text-xs text-muted-foreground">
                    URL del canal Telegram gratis y base del checkout VIP.
                    Se usan en <strong className="text-muted-foreground">todas las superficies</strong>: landing simple, home, pantalla final de Reels y botones del overlay video.
                  </p>
                </div>
                <div className="space-y-2 rounded-xl border border-white/10 bg-zinc-900/40 p-3">
                  <div className="flex items-center justify-between gap-2">
                    <div className="flex items-center gap-1.5">
                      <Label htmlFor="free-tg-enabled">Elemento free-telegram</Label>
                      <HelpHint label="Mostrar u ocultar el free-telegram en todo el sitio">
                        Toggle maestro. <strong>OFF</strong> oculta el free-telegram en TODAS las superficies (card, botones y video Mux) — el usuario solo verá el checkout de pago. <strong>ON</strong> lo muestra según el destino de abajo y las reglas por superficie. Argentina siempre va directo a Stripe (sin free-telegram), sin importar este toggle.
                      </HelpHint>
                    </div>
                    <Switch id="free-tg-enabled" checked={freeTelegramEnabled} onCheckedChange={setFreeTelegramEnabled} />
                  </div>
                  <p className="text-[10.5px] leading-tight text-zinc-500">
                    {freeTelegramEnabled
                      ? "Visible · se muestra según el destino y las reglas por superficie."
                      : "Oculto en todo el sitio · el usuario solo verá el checkout de pago."}
                  </p>
                </div>
                <div className="space-y-2 rounded-xl border border-white/10 bg-zinc-900/40 p-3">
                  <div className="flex items-center justify-between gap-2">
                    <div className="flex items-center gap-1.5">
                      <Label htmlFor="free-access-cta-enabled">Botón "Acceso gratis" (registro + aprobación)</Label>
                      <HelpHint label="CTA secundario en /invitacion-exclusive">
                        <strong>ON</strong> muestra un botón secundario debajo del CTA de membresía en <code>/invitacion-exclusive</code>. El visitante crea su cuenta (Clerk) → completa su perfil en <code>/portal/completar-perfil</code> → queda en <code>/pending-approval</code> esperando aprobación. La solicitud aparece en <strong>/admin/memberships</strong> con nivel <code>free</code>; al aprobarla le asignás el link privado de Telegram. Capturás el usuario aunque no pague.
                      </HelpHint>
                    </div>
                    <Switch
                      id="free-access-cta-enabled"
                      checked={freeAccessCtaEnabled}
                      onCheckedChange={setFreeAccessCtaEnabled}
                    />
                  </div>
                  <p className="text-[10.5px] leading-tight text-zinc-500">
                    {freeAccessCtaEnabled
                      ? "Visible · registro Clerk → perfil → pendiente de aprobación en /admin/memberships."
                      : "Oculto · /invitacion-exclusive muestra solo el CTA de membresía paga."}
                  </p>
                  <div className="space-y-1.5 pt-1">
                    <div className="flex items-center gap-1.5">
                      <Label htmlFor="free-access-cta-label">
                        Label del botón {!freeAccessCtaEnabled && <span className="text-zinc-500">(botón apagado)</span>}
                      </Label>
                      <HelpHint label="Override del texto del botón Acceso gratis">
                        Vacío = traducción por idioma ("Acceso gratis" / "Free access" / "Acesso grátis" / …). Máximo 60 caracteres.
                      </HelpHint>
                    </div>
                    <Input
                      id="free-access-cta-label"
                      value={freeAccessCtaLabel}
                      onChange={(e) => setFreeAccessCtaLabel(e.target.value.slice(0, 60))}
                      placeholder="Acceso gratis"
                      className="border-white/[0.08] bg-zinc-700/50 text-sm"
                    />
                  </div>
                </div>
                <div className="space-y-2">
                  <div className="flex items-center gap-1.5">
                    <Label htmlFor="tg-url">Telegram público gratis {!freeTelegramEnabled && <span className="text-zinc-500">(elemento apagado)</span>}</Label>
                    <HelpHint label="Cadena de fallback del Telegram gratis">
                      Cadena de prioridad: <strong>admin</strong> → <strong>env</strong> (<code>VITE_PREMIUM_SIMPLE_FREE_TELEGRAM_URL</code>) → <strong>BRAND_TELEGRAM_URL</strong>. Si dejás vacío, se usa el siguiente eslabón. Es el <strong>destino</strong> del toggle de arriba.
                    </HelpHint>
                  </div>
                  <Input
                    id="tg-url"
                    value={freeTelegramUrl}
                    onChange={(e) => setFreeTelegramUrl(e.target.value)}
                    placeholder={EFFECTIVE_FREE_TELEGRAM_URL || "https://t.me/…"}
                    className="border-white/[0.08] bg-zinc-700/50 font-mono text-sm"
                  />
                  {!freeTelegramUrl.trim() && (
                    <p className="text-[10.5px] leading-tight text-zinc-500">
                      Vacío · template usa <code className="text-[10px]">{EFFECTIVE_FREE_TELEGRAM_URL || "(ninguno — card no aparece)"}</code>
                    </p>
                  )}
                </div>
                <div className="space-y-2">
                  <div className="flex items-center gap-1.5">
                    <Label htmlFor="tg-by-country">Telegram gratis por país</Label>
                    <HelpHint label="Override por país del Telegram gratis">
                      Un país por línea: <code>US: https://t.me/...</code>. Dejá vacío para heredar la URL global. Prioridad: país → admin global → env → BRAND_TELEGRAM_URL.
                    </HelpHint>
                  </div>
                  <Textarea
                    id="tg-by-country"
                    value={freeTelegramByCountryEditor}
                    onChange={(e) => setFreeTelegramByCountryEditor(e.target.value)}
                    placeholder={"US: https://t.me/exampleus\nES: https://t.me/examplees"}
                    rows={4}
                    className="border-white/[0.08] bg-zinc-700/50 font-mono text-xs"
                  />
                  {!freeTelegramByCountryEditor.trim() && (
                    <p className="text-[10.5px] leading-tight text-zinc-500">
                      Vacío · todos los países usan la URL global de Telegram gratis
                    </p>
                  )}
                </div>
                <div className="space-y-2">
                  <div className="flex items-center gap-1.5">
                    <Label htmlFor="dual-cta-free-label">Label botón "Canal gratis" (Dual CTA)</Label>
                    <HelpHint label="Override del texto del botón secundario en DualCTAVideoLanding">
                      Vacío = usar la traducción por idioma (ej. "Free channel" / "Canal gratis"). Máximo 80 caracteres.
                    </HelpHint>
                  </div>
                  <Input
                    id="dual-cta-free-label"
                    value={dualCtaFreeChannelLabel}
                    onChange={(e) => setDualCtaFreeChannelLabel(e.target.value)}
                    placeholder="Canal gratis"
                    className="border-white/[0.08] bg-zinc-700/50 text-sm"
                  />
                  {!dualCtaFreeChannelLabel.trim() && (
                    <p className="text-[10.5px] leading-tight text-zinc-500">
                      Vacío · usa traducción por idioma
                    </p>
                  )}
                </div>
                <div className="space-y-2">
                  <div className="flex items-center gap-1.5">
                    <Label htmlFor="vip-base">Base checkout VIP</Label>
                    <HelpHint label="Base URL del checkout VIP">
                      Si dejás vacío se usa <code>BRAND_FAN_URL/subscripcion</code>. Se agrega automáticamente <code>?plan=vip&country=…</code>.
                    </HelpHint>
                  </div>
                  <Input
                    id="vip-base"
                    value={vipBaseUrl}
                    onChange={(e) => setVipBaseUrl(e.target.value)}
                    placeholder={EFFECTIVE_VIP_BASE_URL}
                    className="border-white/[0.08] bg-zinc-700/50 font-mono text-sm"
                  />
                  {!vipBaseUrl.trim() && (
                    <p className="text-[10.5px] leading-tight text-zinc-500">
                      Vacío · template usa <code className="text-[10px]">{EFFECTIVE_VIP_BASE_URL}</code>
                    </p>
                  )}
                </div>
              </div>

              <div className="rounded-2xl border border-white/[0.07] bg-zinc-800/30 p-5 space-y-4">
                <div>
                  <div className="flex flex-wrap items-center gap-2">
                    <h2 className="text-sm font-semibold text-foreground">Tarjeta Mi VIP (funnel friendly)</h2>
                    <SurfaceBadge surface="simple" />
                    <SurfaceBadge surface="home" />
                  </div>
                  <p className="mt-1 text-xs text-muted-foreground">
                    Controla el cartel y el copy de la <strong className="text-muted-foreground">card Mi VIP expandida</strong> (cuando el usuario toca el video de membresía).
                    Por defecto se usan{" "}
                    <code className="text-[10px]">VITE_MUX_CTA_FRIENDLY_BANNER</code> y{" "}
                    <code className="text-[10px]">VITE_MUX_CTA_FREE_JOIN_COPY</code> del deploy (Railway).
                  </p>
                </div>
                <div className="grid gap-4 sm:grid-cols-2">
                  <div className="space-y-2">
                    <Label>Cartel "no es VIP" / chip</Label>
                    <Select value={muxFriendlyBanner} onValueChange={(v: MuxTriState) => setMuxFriendlyBanner(v)}>
                      <SelectTrigger className="border-white/[0.08] bg-zinc-700/50"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="inherit">Heredar env</SelectItem>
                        <SelectItem value="on">Forzar activado</SelectItem>
                        <SelectItem value="off">Forzar desactivado</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label>Copy CTA "unirte gratis"</Label>
                    <Select value={muxFreeJoinCopy} onValueChange={(v: MuxTriState) => setMuxFreeJoinCopy(v)}>
                      <SelectTrigger className="border-white/[0.08] bg-zinc-700/50"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="inherit">Heredar env</SelectItem>
                        <SelectItem value="on">Forzar activado</SelectItem>
                        <SelectItem value="off">Forzar desactivado</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="friendly-badge">Texto chip Mi VIP</Label>
                  <Textarea
                    id="friendly-badge"
                    value={friendlyBadgeText}
                    onChange={(e) => setFriendlyBadgeText(e.target.value)}
                    placeholder="Vacío = usar traducciones del sitio"
                    rows={2}
                    className="border-white/[0.08] bg-zinc-700/50 text-sm resize-y min-h-[2.5rem]"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="friendly-disclaimer">Aviso encima de Mi VIP</Label>
                  <Textarea
                    id="friendly-disclaimer"
                    value={friendlyDisclaimerText}
                    onChange={(e) => setFriendlyDisclaimerText(e.target.value)}
                    placeholder="Vacío = usar traducciones del sitio"
                    rows={4}
                    className="border-white/[0.08] bg-zinc-700/50 text-sm resize-y min-h-[5rem]"
                  />
                </div>
              </div>

              <div className="rounded-2xl border border-white/[0.07] bg-zinc-800/30 p-5 space-y-4">
                <div>
                  <div className="flex flex-wrap items-center gap-2">
                    <h2 className="text-sm font-semibold text-foreground">Pantalla final de Reels</h2>
                    <SurfaceBadge surface="reels" />
                  </div>
                  <p className="mt-1 text-xs text-muted-foreground">Titular y etiquetas de los CTAs que aparecen <strong className="text-muted-foreground">al terminar el feed de Reels</strong> (no aplica al layout simple).</p>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="end-h">Titular</Label>
                  <Input id="end-h" value={endHeadline} onChange={(e) => setEndHeadline(e.target.value)} className="border-white/[0.08] bg-zinc-700/50" />
                </div>
                <div className="grid gap-4 sm:grid-cols-2">
                  <div className="space-y-2">
                    <Label htmlFor="end-free">Etiqueta CTA Telegram</Label>
                    <Input id="end-free" value={endFreeLbl} onChange={(e) => setEndFreeLbl(e.target.value)} className="border-white/[0.08] bg-zinc-700/50" />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="end-vip">Etiqueta CTA VIP</Label>
                    <Input id="end-vip" value={endVipLbl} onChange={(e) => setEndVipLbl(e.target.value)} className="border-white/[0.08] bg-zinc-700/50" />
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>

      </div>{/* /accordion */}

      {/* ── Sticky footer ──────────────────────────────────────────────────── */}
      <div className="sticky bottom-4 -mx-4 -mb-4 mt-2 flex flex-wrap items-center justify-between gap-3 bg-gradient-to-t from-card via-card/95 to-transparent px-4 py-4 sm:-mx-8 sm:-mb-8 sm:px-8">
        <div className="flex flex-wrap items-center gap-3">
          <Button
            type="button"
            onClick={() => void save()}
            disabled={saving || refetching || !isDirty}
            size="lg"
            className={cn(
              "relative gap-2 font-semibold shadow-xl shadow-black/40",
              isDirty
                ? "bg-white text-black hover:bg-white/90"
                : "bg-white/30 text-black/60 cursor-not-allowed",
            )}
          >
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
            Guardar
            {isDirty && !saving && (
              <span
                className="absolute -right-1 -top-1 flex h-2.5 w-2.5"
                aria-hidden
              >
                <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-amber-400 opacity-75" />
                <span className="relative inline-flex h-2.5 w-2.5 rounded-full bg-amber-400" />
              </span>
            )}
          </Button>
          {isDirty && (
            <Button
              type="button"
              variant="ghost"
              size="lg"
              onClick={() => {
                if (window.confirm("¿Descartar los cambios sin guardar?")) {
                  void load({ silent: true });
                }
              }}
              disabled={saving || refetching}
              className="gap-2 text-amber-200 hover:bg-amber-500/10 hover:text-amber-100"
            >
              <RotateCcw className="h-4 w-4" aria-hidden />
              Descartar
            </Button>
          )}
          <Button type="button" variant="secondary" onClick={() => void load({ silent: true })} disabled={refetching || saving || loading || isDirty} className="gap-2 border border-border bg-white/5" title={isDirty ? "Descartá o guardá los cambios primero" : "Recargar desde el servidor"}>
            <RefreshCw className={`h-4 w-4 ${refetching ? "animate-spin" : ""}`} aria-hidden />
            Recargar
          </Button>
          <span className="hidden sm:inline text-xs text-muted-foreground">
            <kbd className="rounded border border-border bg-white/5 px-1">⌘</kbd>+<kbd className="rounded border border-border bg-white/5 px-1">S</kbd>
          </span>
        </div>
        <Button type="button" variant="outline" asChild className="border-white/20 bg-transparent">
          <a href="/invitacion-premium" target="_blank" rel="noopener noreferrer" className="gap-2">
            Abrir invitación <ExternalLink className="h-3.5 w-3.5" />
          </a>
        </Button>
      </div>
    </AdminPageShell>
  );
}
